/**
 * Storybook Production Studio — admin-only.
 * Same password as /admin, mounted at /studio.
 *
 * Sections:
 *   - Login
 *   - Story list (sidebar)
 *   - Workspace: Setup → Photos → Plan → Pages
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, BookOpen, Upload, Sparkles, Trash2, PlusCircle, ArrowUp, ArrowDown, Image as ImageIcon, Wand2, RefreshCw, LogOut, Plus, ChevronRight, Download, Copy, RotateCcw, RotateCw, FlipVertical, Settings, Link as LinkIcon, GripVertical, FileDown, Check, Radio, Layers } from "lucide-react";
import {
  DndContext, PointerSensor, TouchSensor, KeyboardSensor,
  useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, rectSortingStrategy, useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "studio_admin_password";
const MAX_STUDIO_PHOTOS = 100;

const headers = (pw) => ({ headers: { "X-Admin-Password": pw } });

/* ----------------------------- Root page ----------------------------- */
/* ----------------------------- Job polling helper ----------------------------- */
// Generic "kick off then poll" helper used by the long Claude / Nano Banana
// jobs. Returns the resolved job doc when status flips to 'done' / 'failed'.
const pollJob = async (jobId, password, { onTick, intervalMs = 3000, timeoutMs = 5 * 60 * 1000 } = {}) => {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    await new Promise((r) => setTimeout(r, intervalMs));
    let r;
    try {
      r = await axios.get(`${API}/studio/jobs/${jobId}`, headers(password));
    } catch (e) {
      // Network blip — keep polling.
      continue;
    }
    const job = r.data?.job;
    if (!job) continue;
    onTick?.(job);
    if (job.status === "done" || job.status === "failed") return job;
  }
  throw new Error("Job timed out after 5 minutes");
};

const StudioPage = ({ vertical = "pet" }) => {
  const [password, setPassword] = useState(sessionStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(!!sessionStorage.getItem(STORAGE_KEY));
  const [verifying, setVerifying] = useState(false);
  const isCuddle = vertical === "cuddle";

  useEffect(() => {
    document.title = isCuddle
      ? "Cuddle Prints Studio · theSnugglePrints"
      : "Storybook Studio · theSnugglePrints";
  }, [isCuddle]);

  const handleLogin = async (e) => {
    e?.preventDefault();
    if (!password) return;
    setVerifying(true);
    try {
      await axios.post(`${API}/admin/verify`, { password });
      sessionStorage.setItem(STORAGE_KEY, password);
      setAuthed(true);
    } catch {
      toast.error("Invalid password");
    } finally {
      setVerifying(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setPassword("");
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0F1115] text-white flex items-center justify-center p-4">
        <form onSubmit={handleLogin} className="w-full max-w-sm bg-[#161A22] border border-[#262B36] rounded-xl p-8" data-testid="studio-login-form">
          <div className="flex items-center gap-2 text-[#E6C982] mb-6">
            <BookOpen className="w-5 h-5" />
            <span className="text-xs uppercase tracking-[0.3em]">
              {isCuddle ? "Cuddle Prints Studio" : "Production Studio"}
            </span>
          </div>
          <h1 className="text-2xl font-serif mb-2">Welcome back</h1>
          <p className="text-sm text-white/60 mb-6">
            {isCuddle
              ? "Enter the admin password to access the Cuddle Prints studio."
              : "Enter the admin password to access the storybook studio."}
          </p>
          <input
            type="password"
            className="w-full bg-[#0F1115] border border-[#2A2F3A] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#E6C982]"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Admin password"
            autoFocus
            data-testid="studio-login-input"
          />
          <button
            type="submit"
            disabled={verifying}
            className="mt-4 w-full bg-[#E6C982] text-[#0F1115] rounded-md px-3 py-2 text-sm font-semibold disabled:opacity-60"
            data-testid="studio-login-submit"
          >
            {verifying ? "Verifying…" : "Enter Studio"}
          </button>
        </form>
      </div>
    );
  }

  return <StudioWorkspace password={password} onLogout={logout} vertical={vertical} />;
};

export default StudioPage;

/* --------------------------- Workspace shell --------------------------- */
const StudioWorkspace = ({ password, onLogout, vertical = "pet" }) => {
  const [stories, setStories] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  // "master" = show the master-prompts config screen instead of a story.
  const [viewMode, setViewMode] = useState("story");
  const isCuddle = vertical === "cuddle";

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/studio/stories?vertical=${vertical}`, headers(password));
      setStories(r.data.stories || []);
      if (!activeId && (r.data.stories || []).length > 0) {
        setActiveId(r.data.stories[0].id);
      }
    } catch {
      toast.error("Failed to load stories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const createStory = async () => {
    const title = prompt(isCuddle ? "Book title:" : "Story title:");
    if (!title) return;
    const subjectName = prompt(isCuddle ? "Child's name:" : "Pet name:");
    if (!subjectName) return;
    try {
      const r = await axios.post(
        `${API}/studio/stories`,
        { title, pet_name: subjectName, vertical },
        headers(password)
      );
      await refresh();
      setActiveId(r.data.story.id);
    } catch {
      toast.error("Failed to create story");
    }
  };

  const duplicateStory = async (id, e) => {
    e?.stopPropagation();
    try {
      const r = await axios.post(
        `${API}/studio/stories/${id}/duplicate`,
        {},
        { ...headers(password), timeout: 120000 }
      );
      toast.success("Story duplicated");
      await refresh();
      setActiveId(r.data.story.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Duplicate failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1115] text-white grid grid-cols-[280px_1fr]">
      {/* Sidebar */}
      <aside className="border-r border-[#1E232D] flex flex-col">
        <div className="px-5 py-5 border-b border-[#1E232D] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-[#E6C982]" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#E6C982]">
              {isCuddle ? "Cuddle Prints" : "Studio"}
            </span>
          </div>
          <button onClick={onLogout} className="text-white/40 hover:text-white" title="Log out" data-testid="studio-logout-btn">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
        <div className="p-3 space-y-2">
          <button
            onClick={() => setViewMode("master")}
            className={`w-full flex items-center justify-center gap-2 border rounded-md px-3 py-2 text-xs uppercase tracking-wider transition ${
              viewMode === "master"
                ? "bg-[#1E232D] border-[#E6C982] text-[#E6C982]"
                : "border-[#1E232D] text-white/60 hover:text-white hover:border-white/30"
            }`}
            data-testid="studio-master-prompts-btn"
            title="Configure master prompts that seed every new story"
          >
            <Settings className="w-4 h-4" /> Master prompts
          </button>
          <button
            onClick={() => { setViewMode("story"); createStory(); }}
            className="w-full flex items-center justify-center gap-2 bg-[#E6C982] text-[#0F1115] rounded-md px-3 py-2 text-sm font-semibold"
            data-testid="studio-new-story-btn"
          >
            <Plus className="w-4 h-4" /> {isCuddle ? "New Book" : "New Story"}
          </button>
        </div>
        <div className="px-3 pb-3 overflow-y-auto">
          {loading && <div className="text-xs text-white/40 px-2 py-2">Loading…</div>}
          {!loading && stories.length === 0 && (
            <div className="text-xs text-white/40 px-2 py-2">No stories yet. Create your first one above.</div>
          )}
          {stories.map((s) => (
            <div
              key={s.id}
              role="button"
              tabIndex={0}
              onClick={() => { setActiveId(s.id); setViewMode("story"); }}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setActiveId(s.id), setViewMode("story"))}
              className={`group w-full px-3 py-2.5 rounded-md mb-1 transition cursor-pointer ${
                activeId === s.id && viewMode === "story" ? "bg-[#1E232D] text-white" : "text-white/70 hover:bg-[#161A22]"
              }`}
              data-testid={`studio-story-row-${s.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-medium truncate flex-1">{s.title || "Untitled"}</div>
                <button
                  onClick={(e) => duplicateStory(s.id, e)}
                  className="opacity-0 group-hover:opacity-100 text-white/40 hover:text-white transition"
                  title="Duplicate story"
                  data-testid={`studio-duplicate-story-${s.id}`}
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
                <ChevronRight className="w-3 h-3 text-white/40 flex-shrink-0" />
              </div>
              <div className="text-[11px] text-white/40 mt-0.5 truncate">
                {s.pet_name} · {s.page_count} pages · {s.status}
                {typeof s.credits_cost_usd === "number" && s.credits_cost_usd > 0 && (
                  <span className="ml-1 text-[#E6C982]/80">
                    · ${s.credits_cost_usd.toFixed(2)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </aside>

      {/* Main pane */}
      <main className="overflow-y-auto">
        {viewMode === "master" ? (
          <MasterPromptsPanel password={password} onClose={() => setViewMode("story")} />
        ) : activeId ? (
          <StoryEditor
            key={activeId}
            storyId={activeId}
            password={password}
            onMutate={refresh}
            vertical={vertical}
            onDelete={async () => {
              if (!confirm("Delete this entire story?")) return;
              await axios.delete(`${API}/studio/stories/${activeId}`, headers(password));
              setActiveId(null);
              refresh();
            }}
          />
        ) : (
          <div className="h-full flex items-center justify-center text-white/40 text-sm">
            Select or create a story to begin.
          </div>
        )}
      </main>
    </div>
  );
};

/* ----------------------------- Master prompts ----------------------------- */
// A studio-wide singleton config. New stories seed from these values on
// creation; existing stories are NOT touched when this doc changes.
const MasterPromptsPanel = ({ password, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [master, setMaster] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [draft, setDraft] = useState({});
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/studio/settings/master-prompts`, headers(password));
      const m = r.data?.master || {};
      const d = r.data?.defaults || {};
      setMaster(m);
      setDefaults(d);
      setUpdatedAt(m.updated_at || null);
      setDraft({
        custom_style_prompt: m.custom_style_prompt || "",
        custom_layout_instructions: m.custom_layout_instructions || "",
        custom_planner_tone: m.custom_planner_tone || "",
        custom_prompts: { ...(m.custom_prompts || {}) },
        image_providers: [...(m.image_providers || ["nano_banana"])],
      });
    } catch {
      toast.error("Failed to load master prompts");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handlePromptKey = (key) => (e) => setDraft((d) => ({
    ...d,
    custom_prompts: { ...(d.custom_prompts || {}), [key]: e.target.value },
  }));
  const resetPromptKey = (key) => () => setDraft((d) => {
    const next = { ...(d.custom_prompts || {}) };
    delete next[key];
    return { ...d, custom_prompts: next };
  });
  const toggleProvider = (pid) => {
    const cur = new Set(draft.image_providers || []);
    if (cur.has(pid)) cur.delete(pid); else cur.add(pid);
    const next = Array.from(cur);
    setDraft((d) => ({ ...d, image_providers: next.length ? next : [pid] }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/studio/settings/master-prompts`, draft, headers(password));
      toast.success("Master prompts saved · applies to NEW stories only");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !defaults) {
    return <div className="p-8 text-white/50 text-sm">Loading master config…</div>;
  }
  const activeProviders = new Set(draft.image_providers || []);
  const customPrompts = draft.custom_prompts || {};
  return (
    <div className="p-8 max-w-4xl">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#E6C982]/70 mb-1">Studio-wide</div>
          <h1 className="text-3xl font-serif" data-testid="studio-master-title">Master prompts</h1>
          <p className="text-sm text-white/50 mt-1 max-w-2xl">
            These values are the starting point for every NEW story you create. Changing them here does <span className="text-white/70 font-medium">not</span> retroactively update existing stories — each story keeps whatever prompts it was created with (which you can still edit per-story in the Setup tab).
          </p>
          {updatedAt && (
            <p className="text-[11px] text-white/30 mt-1">Last saved · {new Date(updatedAt).toLocaleString()}</p>
          )}
        </div>
        <button onClick={onClose} className="text-white/40 hover:text-white text-sm" data-testid="studio-master-close">Close</button>
      </div>

      <div className="space-y-6">
        <SectionCard
          title="Default image models"
          hint="New stories will render with this set of providers unless the operator changes it in the story Setup."
        >
          <div className="grid grid-cols-2 gap-3">
            {PROVIDERS.map((p) => {
              const on = activeProviders.has(p.id);
              return (
                <button
                  key={p.id}
                  onClick={() => toggleProvider(p.id)}
                  className={`text-left p-3 border rounded-md transition ${on ? "border-[#E6C982] bg-[#E6C982]/5" : "border-[#1E232D] hover:border-white/30"}`}
                  data-testid={`studio-master-provider-${p.id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">{p.label}</span>
                    <span className={`text-[10px] ${on ? "text-[#E6C982]" : "text-white/30"}`}>{on ? "ON" : "OFF"}</span>
                  </div>
                  <p className="text-[11px] text-white/50">{p.tag}</p>
                </button>
              );
            })}
          </div>
        </SectionCard>

        <SectionCard
          title="Story-wide overrides"
          hint="Style, layout, and planner-tone strings that get concatenated into most Claude / image-gen prompts. Leave blank to use the built-in defaults shown as placeholders."
        >
          <PromptEditor
            label="Global style prompt (locked art direction)"
            placeholder={defaults.default_style_prompt}
            value={draft.custom_style_prompt || ""}
            onChange={(e) => setDraft((d) => ({ ...d, custom_style_prompt: e.target.value }))}
            testId="studio-master-style"
          />
          <PromptEditor
            label="Global layout instructions"
            placeholder={defaults.default_layout_instructions}
            value={draft.custom_layout_instructions || ""}
            onChange={(e) => setDraft((d) => ({ ...d, custom_layout_instructions: e.target.value }))}
            testId="studio-master-layout"
          />
          <PromptEditor
            label="Story planner tone (Claude)"
            placeholder={defaults.default_planner_tone}
            value={draft.custom_planner_tone || ""}
            onChange={(e) => setDraft((d) => ({ ...d, custom_planner_tone: e.target.value }))}
            testId="studio-master-planner-tone"
          />
        </SectionCard>

        <SectionCard
          title="Full prompt library (power users)"
          hint="Raw system + user templates the AI sees for every generation. Leave a field blank to fall back to the built-in default (shown as the greyed-out placeholder). Placeholders like {text} or {pet_label} are filled at runtime — keep them as-is."
        >
          {PROMPT_LIBRARY.map(({ key, label, hint, placeholders }) => (
            <FullPromptEditor
              key={key}
              promptKey={key}
              label={label}
              hint={hint}
              placeholders={placeholders}
              defaultText={defaults.default_prompts[key] || ""}
              value={customPrompts[key] ?? ""}
              onChange={handlePromptKey(key)}
              onReset={resetPromptKey(key)}
            />
          ))}
        </SectionCard>

        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={save}
            disabled={saving}
            className="bg-[#E6C982] text-[#0F1115] rounded-md px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
            data-testid="studio-master-save"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Save master prompts
          </button>
          <span className="text-[11px] text-white/40">Applies to NEW stories created after saving. Existing stories are unaffected.</span>
        </div>
      </div>
    </div>
  );
};

/* ----------------------------- Story editor ----------------------------- */
const StoryEditor = ({ storyId, password, onMutate, onDelete, vertical = "pet" }) => {
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("setup");
  const [planning, setPlanning] = useState(false);
  const [aiDecideOrder, setAiDecideOrder] = useState(false);
  const [showDownload, setShowDownload] = useState(false);
  // Tracks whether the initial fetch for this story has completed. All
  // subsequent reloads (after save/upload/generate/etc.) refresh the doc
  // silently without unmounting the editor or forcing the tab back — the
  // page scroll position stays wherever the admin left it.
  const hasLoadedRef = useRef(false);

  const load = async () => {
    const isFirstLoad = !hasLoadedRef.current;
    if (isFirstLoad) setLoading(true);
    try {
      const r = await axios.get(`${API}/studio/stories/${storyId}`, headers(password));
      setStory(r.data.story);
      if (isFirstLoad) {
        // Auto-jump to the most useful tab on FIRST load only. Otherwise
        // we would fling the admin out of Setup/Cover/Pages every time
        // they save a field.
        if (r.data.story.pages?.length) setTab("pages");
        else if (r.data.story.photos?.length) setTab("plan");
        else setTab("photos");
        hasLoadedRef.current = true;
      }
    } catch {
      toast.error("Failed to load story");
    } finally {
      if (isFirstLoad) setLoading(false);
    }
  };
  useEffect(() => {
    hasLoadedRef.current = false;
    load();
  }, [storyId]);

  const saveMeta = async (patch) => {
    setStory((s) => (s ? { ...s, ...patch } : s));
    try {
      await axios.put(`${API}/studio/stories/${storyId}`, patch, headers(password));
      onMutate?.();
    } catch {
      toast.error("Failed to save");
    }
  };

  const runPlan = async () => {
    if (!story?.photos?.length) {
      toast.error("Upload at least 1 photo first");
      return;
    }
    if (story.pages?.length && !confirm("This will replace existing pages with a fresh AI plan. Continue?")) return;
    setPlanning(true);
    try {
      // New: backend returns 202 + job_id immediately; we poll for status.
      const r = await axios.post(`${API}/studio/stories/${storyId}/plan`, { ai_decides_order: aiDecideOrder }, headers(password));
      const jobId = r.data?.job_id;
      if (!jobId) throw new Error("No job id returned");
      const job = await pollJob(jobId, password);
      if (job.status !== "done") throw new Error(job.error || "Planning failed");
      await load();
      setTab("pages");
      toast.success(`Plan generated · ${job.result?.pages || 0} pages`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Planning failed");
    } finally {
      setPlanning(false);
    }
  };

  if (loading || !story) {
    return <div className="p-10 text-white/40 text-sm">Loading…</div>;
  }

  return (
    <div className="max-w-[1100px] mx-auto px-8 py-8" data-testid="studio-story-editor">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#E6C982]/70 mb-1">{story.status}</div>
          <h1 className="text-3xl font-serif" data-testid="studio-story-title">{story.title || "Untitled"}</h1>
          {story.summary && <p className="text-sm text-white/60 mt-1 max-w-xl">{story.summary}</p>}
        </div>
        <div className="flex items-center gap-4">
          <CreditsBadge story={story} />
          <ShareLinkButton storyId={storyId} password={password} shareToken={story?.share_token} onChanged={load} />
          <PreviewLinkButton storyId={storyId} password={password} previewToken={story?.preview_token} onChanged={load} />
          <button
            onClick={() => setShowDownload(true)}
            className="text-white/70 hover:text-[#E6C982] text-sm inline-flex items-center gap-1 border border-[#1E232D] hover:border-[#E6C982]/50 rounded-md px-3 py-1.5 transition"
            data-testid="studio-download-book-btn"
          >
            <Download className="w-3.5 h-3.5" /> Download book
          </button>
          <button onClick={onDelete} className="text-white/40 hover:text-red-300 text-sm inline-flex items-center gap-1" data-testid="studio-delete-story-btn">
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      </div>

      {showDownload && (
        <DownloadBookDialog
          storyId={storyId}
          password={password}
          onClose={() => setShowDownload(false)}
        />
      )}

      <div className="flex items-center gap-1 mb-6 border-b border-[#1E232D] overflow-x-auto">
        {[
          { id: "setup", label: "setup" },
          { id: "photos", label: "photos" },
          { id: "favorites", label: "Favorite Toys & Things" },
          { id: "plan", label: "plan" },
          { id: "pages", label: "pages" },
          { id: "empty_pages", label: "Empty Pages" },
          { id: "love_page", label: "Page 17" },
          { id: "cover", label: "cover" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm uppercase tracking-wider whitespace-nowrap ${
              tab === t.id ? "text-[#E6C982] border-b-2 border-[#E6C982] -mb-px" : "text-white/40 hover:text-white"
            }`}
            data-testid={`studio-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "setup" && <SetupPanel story={story} saveMeta={saveMeta} vertical={vertical} />}
      {tab === "photos" && (
        <PhotosPanel
          story={story}
          password={password}
          onReload={load}
        />
      )}
      {tab === "favorites" && (
        <FavoriteThingsPanel story={story} password={password} onReload={load} />
      )}
      {tab === "plan" && (
        <PlanPanel story={story} planning={planning} runPlan={runPlan} setTab={setTab} aiDecideOrder={aiDecideOrder} setAiDecideOrder={setAiDecideOrder} />
      )}
      {tab === "pages" && (
        <PagesPanel story={story} password={password} onReload={load} />
      )}
      {tab === "empty_pages" && (
        <EmptyPagesPanel story={story} password={password} onReload={load} />
      )}
      {tab === "love_page" && (
        <LovePagePanel story={story} password={password} onReload={load} />
      )}
      {tab === "cover" && (
        <CoverPanel story={story} password={password} onReload={load} />
      )}
    </div>
  );
};

/* ----------------------------- Setup tab ----------------------------- */
const PROVIDERS = [
  { id: "nano_banana", label: "Nano Banana", tag: "Preserves photo composition · faster (~30s)" },
  { id: "gpt_image_1", label: "GPT Image 1", tag: "Cleaner text · text-only prompt (~60s)" },
];

// Friendly labels for each credit field the backend tracks. Keep in sync
// with CREDIT_FIELDS in /app/backend/studio.py.
const CREDIT_ROWS = [
  { key: "planner_runs", label: "Claude story plans" },
  { key: "direction_runs", label: "Illustration directions" },
  { key: "title_suggestions", label: "Cover title suggestions" },
  { key: "text_regens", label: "Page text regens" },
  { key: "images_nano_banana", label: "Nano Banana page illustrations" },
  { key: "images_gpt_image_1", label: "GPT Image 1 page illustrations" },
  { key: "edits_nano_banana", label: "Nano Banana edits" },
  { key: "edits_gpt_image_1", label: "GPT Image 1 edits" },
  { key: "cover_nano_banana", label: "Nano Banana cover renders" },
  { key: "cover_gpt_image_1", label: "GPT Image 1 cover renders" },
];

const formatUsd = (n) => `$${(n || 0).toFixed(2)}`;

const CreditsBadge = ({ story }) => {
  const credits = story?.credits || {};
  const totalOps = CREDIT_ROWS.reduce((sum, r) => sum + (credits[r.key] || 0), 0);
  const cost = story?.credits_cost_usd || 0;
  return (
    <div
      className="hidden sm:flex flex-col items-end text-right"
      title="Estimated AI spend so far — hover the counters in Setup for the full breakdown"
      data-testid="studio-credits-badge"
    >
      <div className="text-[10px] uppercase tracking-wider text-white/40">AI credits used</div>
      <div className="text-sm font-semibold text-[#E6C982]" data-testid="studio-credits-cost">
        {formatUsd(cost)}
      </div>
      <div className="text-[10px] text-white/40">
        {totalOps} AI operation{totalOps === 1 ? "" : "s"}
      </div>
    </div>
  );
};

const CreditsPanel = ({ story }) => {
  const credits = story?.credits || {};
  const units = story?.credit_cost_unit_usd || {};
  const totalOps = CREDIT_ROWS.reduce((sum, r) => sum + (credits[r.key] || 0), 0);
  const totalCost = story?.credits_cost_usd || 0;
  return (
    <SectionCard
      title="AI credits used on this photobook"
      hint="Every Claude planning run and every image generated by Nano Banana / GPT Image 1 counts here. Cost is a running estimate using published unit prices — actual billing lives on the Emergent Universal Key."
    >
      <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs" data-testid="studio-credits-breakdown">
        {CREDIT_ROWS.map((r) => {
          const n = credits[r.key] || 0;
          const unit = units[r.key] || 0;
          const rowCost = n * unit;
          const dim = n === 0;
          return (
            <div
              key={r.key}
              className={`flex items-baseline justify-between border-b border-[#1E232D] py-1.5 ${
                dim ? "text-white/30" : "text-white/80"
              }`}
              data-testid={`studio-credit-row-${r.key}`}
            >
              <div>
                <span className="tabular-nums font-mono mr-2">{n}</span>
                <span>{r.label}</span>
              </div>
              <div className="text-white/40 tabular-nums font-mono">
                {formatUsd(rowCost)}
                <span className="text-[10px] text-white/30 ml-1">
                  ({formatUsd(unit)} ea)
                </span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-baseline justify-between pt-3 border-t border-[#2A2F3A] mt-2">
        <div className="text-sm text-white/60">
          Total <span className="text-white/40">· {totalOps} operation{totalOps === 1 ? "" : "s"}</span>
        </div>
        <div className="text-base font-semibold text-[#E6C982] tabular-nums" data-testid="studio-credits-total">
          {formatUsd(totalCost)}
        </div>
      </div>
    </SectionCard>
  );
};

const SetupPanel = ({ story, saveMeta, vertical = "pet" }) => {
  const isCuddle = (vertical === "cuddle") || (story?.vertical === "cuddle");
  const [draft, setDraft] = useState(story);
  useEffect(() => { setDraft(story); }, [story.id]);
  const handle = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));
  const handlePromptKey = (key) => (e) => setDraft((d) => ({
    ...d,
    custom_prompts: { ...(d.custom_prompts || {}), [key]: e.target.value },
  }));
  const resetPromptKey = (key) => () => setDraft((d) => {
    const next = { ...(d.custom_prompts || {}) };
    delete next[key];
    return { ...d, custom_prompts: next };
  });
  const toggleProvider = (pid) => {
    const cur = new Set(draft.image_providers || []);
    if (cur.has(pid)) cur.delete(pid); else cur.add(pid);
    // Never allow zero providers.
    const next = Array.from(cur);
    setDraft((d) => ({ ...d, image_providers: next.length ? next : [pid] }));
  };
  const handleSave = () => {
    const patch = {};
    [
      "title", "customer_name", "customer_email", "customer_phone",
      "pet_name", "pet_species", "pet_breed", "pet_gender", "pet_notes",
      "image_aspect_ratio", "pet_tense",
      "custom_style_prompt", "custom_layout_instructions", "custom_planner_tone",
    ].forEach((k) => {
      if ((draft[k] ?? "") !== (story[k] ?? "")) patch[k] = draft[k] ?? "";
    });
    const oldPs = (story.image_providers || []).slice().sort().join(",");
    const newPs = (draft.image_providers || []).slice().sort().join(",");
    if (oldPs !== newPs) patch.image_providers = draft.image_providers;
    // Custom prompt library — send the whole dict when anything changed.
    const oldCp = JSON.stringify(story.custom_prompts || {});
    const newCp = JSON.stringify(draft.custom_prompts || {});
    if (oldCp !== newCp) patch.custom_prompts = draft.custom_prompts || {};
    if (Object.keys(patch).length) { saveMeta(patch); toast.success("Saved"); }
  };
  const activeProviders = new Set(draft.image_providers || []);
  const defaultPrompts = story.default_prompts || {};
  const customPrompts = draft.custom_prompts || {};
  return (
    <div className="max-w-3xl space-y-8">
      <CreditsPanel story={story} />
      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
        <Field label="Story title"><input className={inputCls} value={draft.title || ""} onChange={handle("title")} data-testid="studio-setup-title" /></Field>
        <Field label={isCuddle ? "Child's name" : "Pet name"}><input className={inputCls} value={draft.pet_name || ""} onChange={handle("pet_name")} data-testid="studio-setup-pet-name" /></Field>
        <Field label="Customer name"><input className={inputCls} value={draft.customer_name || ""} onChange={handle("customer_name")} /></Field>
        <Field label="Customer email"><input className={inputCls} value={draft.customer_email || ""} onChange={handle("customer_email")} /></Field>
        <Field label="Customer phone"><input className={inputCls} value={draft.customer_phone || ""} onChange={handle("customer_phone")} /></Field>
        {isCuddle ? (
          <>
            <Field label="Relationship (e.g. Mother, Grandparent)"><input className={inputCls} value={draft.pet_species || ""} onChange={handle("pet_species")} placeholder="Mother / Father / Grandparent…" /></Field>
            <Field label="Child's age"><input className={inputCls} value={draft.pet_breed || ""} onChange={handle("pet_breed")} placeholder="e.g. 5 years" /></Field>
          </>
        ) : (
          <>
            <Field label="Pet species"><input className={inputCls} value={draft.pet_species || ""} onChange={handle("pet_species")} placeholder="dog / cat / ..." /></Field>
            <Field label="Pet breed"><input className={inputCls} value={draft.pet_breed || ""} onChange={handle("pet_breed")} /></Field>
            <Field label="Pet gender"><input className={inputCls} value={draft.pet_gender || ""} onChange={handle("pet_gender")} placeholder="m / f / —" /></Field>
          </>
        )}
        <div className="col-span-2">
          <Field label={isCuddle ? "About the child & your bond (personality, memories, what makes them special)" : "Pet notes (quirks, personality, recurring people)"}>
            <textarea rows={3} className={inputCls + " min-h-[90px]"} value={draft.pet_notes || ""} onChange={handle("pet_notes")} />
          </Field>
        </div>
        {!isCuddle && (
        <div className="col-span-2">
          <Field label="Pet status (controls text tense throughout the book)">
            <div className="flex gap-3 mt-1">
              {[
                { value: "present", label: "Pet is alive", sub: "Present tense — 'he loves', 'she runs'" },
                { value: "past", label: "Pet has passed away", sub: "Past tense — 'he loved', 'she ran'" },
              ].map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-start gap-2.5 p-3 rounded-md border cursor-pointer flex-1 transition ${
                    (draft.pet_tense || "present") === opt.value
                      ? "border-[#E6C982] bg-[#E6C982]/5"
                      : "border-[#1E232D] hover:border-white/20"
                  }`}
                  data-testid={`studio-pet-tense-${opt.value}`}
                >
                  <input
                    type="radio"
                    name="pet_tense"
                    value={opt.value}
                    checked={(draft.pet_tense || "present") === opt.value}
                    onChange={() => setDraft((d) => ({ ...d, pet_tense: opt.value }))}
                    className="mt-0.5 accent-[#E6C982]"
                  />
                  <div>
                    <div className="text-sm font-medium text-white">{opt.label}</div>
                    <div className="text-[11px] text-white/50">{opt.sub}</div>
                  </div>
                </label>
              ))}
            </div>
          </Field>
        </div>
        )}
        <div className="col-span-2">
          <Field label="Image aspect ratio">
            <input
              className={inputCls}
              value={draft.image_aspect_ratio || ""}
              onChange={handle("image_aspect_ratio")}
              placeholder="A5 landscape"
              data-testid="studio-setup-aspect-ratio"
            />
            <p className="text-[11px] text-white/40 mt-1.5 leading-relaxed">
              Controls the canvas ratio for every generated illustration in this book.
              Leave blank for the default (A5 landscape).
              Supported: <span className="text-white/60">A5 landscape</span> · <span className="text-white/60">A4 portrait</span> · <span className="text-white/60">square</span> · <span className="text-white/60">16:9</span> · <span className="text-white/60">3:2</span> · <span className="text-white/60">1024x1536</span>
            </p>
          </Field>
        </div>
      </div>

      <SectionCard
        title="Image generation model(s)"
        hint="Pick one or both. When both are selected, each Generate click produces two images — one per model — for side-by-side comparison. You can still override on a per-page basis later."
      >
        <div className="grid grid-cols-2 gap-3">
          {PROVIDERS.map((p) => (
            <label
              key={p.id}
              className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition ${
                activeProviders.has(p.id)
                  ? "border-[#E6C982] bg-[#E6C982]/5"
                  : "border-[#1E232D] hover:border-white/20"
              }`}
              data-testid={`studio-setup-provider-${p.id}`}
            >
              <input
                type="checkbox"
                checked={activeProviders.has(p.id)}
                onChange={() => toggleProvider(p.id)}
                className="mt-0.5 accent-[#E6C982]"
              />
              <div>
                <div className="text-sm font-medium text-white">{p.label}</div>
                <div className="text-[11px] text-white/50">{p.tag}</div>
              </div>
            </label>
          ))}
        </div>
      </SectionCard>

      <SectionCard
        title="AI prompt overrides (advanced)"
        hint="These prompts control how Claude writes the story and how the illustrator interprets each page. Leave blank to use the built-in defaults shown as placeholders."
      >
        <PromptEditor
          label="Illustration style"
          placeholder={story.default_style_prompt || ""}
          value={draft.custom_style_prompt || ""}
          onChange={handle("custom_style_prompt")}
          testId="studio-setup-style-prompt"
        />
        <PromptEditor
          label="Illustration layout / composition"
          placeholder={story.default_layout_instructions || ""}
          value={draft.custom_layout_instructions || ""}
          onChange={handle("custom_layout_instructions")}
          testId="studio-setup-layout-prompt"
        />
        <PromptEditor
          label="Story planner tone (Claude)"
          placeholder={story.default_planner_tone || ""}
          value={draft.custom_planner_tone || ""}
          onChange={handle("custom_planner_tone")}
          testId="studio-setup-planner-tone"
        />
      </SectionCard>

      <SectionCard
        title="Full prompt library (power users)"
        hint="These are the raw system + user templates the AI sees for every generation. Leave a field blank to fall back to the built-in default (shown as the greyed-out placeholder). Placeholders like {text} or {pet_label} are filled at runtime — keep them as-is."
      >
        {PROMPT_LIBRARY.map(({ key, label, hint, placeholders }) => (
          <FullPromptEditor
            key={key}
            promptKey={key}
            label={label}
            hint={hint}
            placeholders={placeholders}
            defaultText={defaultPrompts[key] || ""}
            value={customPrompts[key] ?? ""}
            onChange={handlePromptKey(key)}
            onReset={resetPromptKey(key)}
          />
        ))}
      </SectionCard>

      <div className="flex gap-2">
        <button onClick={handleSave} className="bg-[#E6C982] text-[#0F1115] rounded-md px-4 py-2 text-sm font-semibold" data-testid="studio-setup-save">Save all changes</button>
      </div>
    </div>
  );
};

const SectionCard = ({ title, hint, children }) => (
  <div className="bg-[#161A22] border border-[#1E232D] rounded-xl p-5">
    <div className="text-sm font-medium text-white mb-1">{title}</div>
    {hint && <p className="text-[11px] text-white/50 mb-4 max-w-2xl">{hint}</p>}
    <div className="space-y-4">{children}</div>
  </div>
);

const PromptEditor = ({ label, placeholder, value, onChange, testId }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-white/40 mb-1.5">{label}</div>
    <textarea
      rows={3}
      className={inputCls + " min-h-[80px] text-xs"}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testId}
    />
  </div>
);

// Ordered list of every editable prompt key exposed by the backend's
// DEFAULT_PROMPTS dict. Keep in sync with /app/backend/studio.py.
const PROMPT_LIBRARY = [
  {
    key: "illustration_task",
    label: "Page illustration · task",
    hint: "Main instruction sent to the image model for every page. Governs how the reference photo is transformed and how the landscape canvas is filled.",
  },
  {
    key: "text_baking",
    label: "Page illustration · bake page text",
    hint: "Wraps the page text so the model paints it as hand-lettering inside the artwork.",
    placeholders: ["{text}"],
  },
  {
    key: "edit_append",
    label: "Page illustration · edit instruction",
    hint: "Appended to the base prompt whenever an admin refines an illustration via 'Edit'.",
    placeholders: ["{edit}"],
  },
  {
    key: "direction_system",
    label: "Illustration direction · system",
    hint: "Art-director prompt Claude uses to write the '### Illustration Direction' bullets for each page. Keep the output format instructions intact — the frontend renders the markdown as-is.",
  },
  {
    key: "direction_user_template",
    label: "Illustration direction · user template",
    hint: "Message sent alongside each page's photo when Claude drafts its illustration direction. `{caption}` is the admin's description of the photo.",
    placeholders: ["{pet_label}", "{pet_species_breed}", "{caption}"],
  },
  {
    key: "cover_front_task",
    label: "Front cover · task",
    hint: "Front cover version of the illustration task.",
  },
  {
    key: "cover_back_task",
    label: "Back cover · task",
    hint: "Back cover version of the illustration task.",
  },
  {
    key: "cover_title_baking",
    label: "Front cover · bake title",
    hint: "Wraps the chosen cover title so the model paints it as hand-lettering on the front cover.",
    placeholders: ["{title}", "{pet_name}"],
  },
  {
    key: "cover_back_notes_baking",
    label: "Back cover · bake notes",
    hint: "Bakes the admin's IG/website/notes into the back cover.",
    placeholders: ["{notes}"],
  },
  {
    key: "planner_system",
    label: "Claude story planner · system",
    hint: "Sets the editorial voice Claude uses when planning the whole storybook. `{tone}` is replaced with the story's tone override.",
    placeholders: ["{tone}"],
  },
  {
    key: "planner_user_template",
    label: "Claude story planner · user template",
    hint: "The full instruction sent to Claude alongside the photos. Must keep the strict-JSON output rules or the pipeline breaks.",
    placeholders: ["{n}", "{n_minus_1}", "{pet_label}", "{pet_species_breed}", "{photo_context}"],
  },
  {
    key: "title_system",
    label: "Title suggester · system",
    placeholders: ["{tone}"],
  },
  {
    key: "title_user_template",
    label: "Title suggester · user template",
    placeholders: ["{pet_label}", "{summary}", "{captions}"],
  },
  {
    key: "text_regen_system",
    label: "Text regen · system",
  },
  {
    key: "text_regen_user_template",
    label: "Text regen · user template",
    placeholders: ["{pet_label}", "{current_text}", "{illustration_prompt}", "{mood}", "{existing_options}", "{count}", "{direction_line}"],
  },
  {
    key: "batch_text_system",
    label: "Batch text (whole book) · system",
    hint: "System prompt Claude uses when writing text for EVERY page in one shot — ensures the copy flows page-to-page as a coherent arc. `{tone}` is replaced with the story's tone override.",
    placeholders: ["{tone}"],
  },
  {
    key: "batch_text_user_template",
    label: "Batch text (whole book) · user template",
    hint: "Instruction sent to Claude alongside all page photos + captions in final order. Must keep the strict-JSON `pages[].text_options[]` output shape.",
    placeholders: ["{n}", "{pet_label}", "{pet_species_breed}", "{pages_context}"],
  },
];

const FullPromptEditor = ({
  promptKey, label, hint, placeholders,
  defaultText, value, onChange, onReset,
}) => {
  const [showDefault, setShowDefault] = useState(false);
  const isOverridden = (value ?? "").length > 0;
  return (
    <div className="border border-[#1E232D] rounded-md p-3">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-white/60">{label}</div>
          {hint && <p className="text-[11px] text-white/40 mt-0.5 max-w-2xl">{hint}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isOverridden && (
            <span className="text-[10px] uppercase tracking-wider text-[#E6C982]">Overridden</span>
          )}
          <button
            type="button"
            onClick={() => setShowDefault((v) => !v)}
            className="text-[11px] text-white/40 hover:text-white underline"
            data-testid={`studio-prompt-${promptKey}-toggle-default`}
          >
            {showDefault ? "Hide default" : "Show default"}
          </button>
          {isOverridden && (
            <button
              type="button"
              onClick={onReset}
              className="text-[11px] text-white/40 hover:text-white underline"
              data-testid={`studio-prompt-${promptKey}-reset`}
            >
              Reset
            </button>
          )}
        </div>
      </div>
      {placeholders?.length ? (
        <div className="text-[10px] text-white/40 mb-1.5">
          Placeholders: <span className="font-mono text-white/60">{placeholders.join(" · ")}</span>
        </div>
      ) : null}
      {showDefault && (
        <pre className="text-[10px] font-mono text-white/40 bg-[#0F1115] border border-[#1E232D] rounded p-2 mb-1.5 whitespace-pre-wrap max-h-40 overflow-y-auto">
{defaultText}
        </pre>
      )}
      <textarea
        rows={Math.min(12, Math.max(3, (defaultText || "").split("\n").length + 1))}
        className={inputCls + " min-h-[80px] text-xs font-mono"}
        value={value}
        onChange={onChange}
        placeholder={defaultText}
        data-testid={`studio-prompt-${promptKey}`}
      />
    </div>
  );
};

/* ----------------------------- Preview Link Button ----------------------------- */
const PreviewLinkButton = ({ storyId, password, previewToken, onChanged }) => {
  const [loading, setLoading] = useState(false);
  const BASE = (process.env.REACT_APP_BACKEND_URL || "").replace("/api", "");

  const generateAndCopy = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/studio/stories/${storyId}/preview-link`, {}, headers(password));
      const token = r.data?.preview_token;
      if (!token) throw new Error("No token");
      const url = `${BASE}/book-preview/${storyId}/${token}`;
      await navigator.clipboard.writeText(url);
      toast.success("Preview link copied to clipboard");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate preview link");
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={generateAndCopy}
      disabled={loading}
      className="text-white/70 hover:text-[#E6C982] text-sm inline-flex items-center gap-1 border border-[#1E232D] hover:border-[#E6C982]/50 rounded-md px-3 py-1.5 transition disabled:opacity-60"
      data-testid="studio-preview-link-btn"
      title={previewToken ? "Preview link exists — click to copy again" : "Generate customer preview link"}
    >
      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LinkIcon className="w-3.5 h-3.5" />}
      {previewToken ? "Copy preview link" : "Share preview"}
    </button>
  );
};

/* ----------------------------- Download Photos Dialog ----------------------------- */
const DownloadPhotosDialog = ({ storyId, password, photoCount, favCount, onClose }) => {
  const [fmt, setFmt] = useState("jpeg");
  const [loading, setLoading] = useState(false);

  const download = async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/studio/stories/${storyId}/download-photos?format=${fmt}`,
        { ...headers(password), responseType: "blob", timeout: 60000 }
      );
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const cd = r.headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      const fname = m ? m[1] : `photos-${storyId}.zip`;
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${fname}`);
      onClose();
    } catch (e) {
      let detail = "Download failed";
      if (e?.response?.data) {
        try { detail = JSON.parse(await e.response.data.text())?.detail || detail; } catch {}
      }
      toast.error(detail);
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[#0F1115] border border-[#1E232D] rounded-lg w-full max-w-sm p-6 space-y-5" data-testid="studio-download-photos-dialog">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-base font-serif">Download all photos</h3>
            <p className="text-xs text-white/50 mt-1">{photoCount} story photo{photoCount !== 1 ? "s" : ""} + {favCount} favorite{favCount !== 1 ? "s" : ""}</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white text-xl px-1">×</button>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-white/40 mb-2">Image format</div>
          <div className="flex gap-2">
            {[{ v: "jpeg", l: "JPEG (smaller, default)" }, { v: "png", l: "PNG (lossless)" }].map((o) => (
              <label key={o.v} className={`flex-1 flex items-center gap-2 p-2.5 rounded border cursor-pointer transition ${fmt === o.v ? "border-[#E6C982] bg-[#E6C982]/5" : "border-[#1E232D] hover:border-white/20"}`}>
                <input type="radio" name="fmt" value={o.v} checked={fmt === o.v} onChange={() => setFmt(o.v)} className="accent-[#E6C982]" />
                <span className="text-xs text-white/80">{o.l}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="text-sm px-4 py-2 border border-[#1E232D] rounded-md text-white/70 hover:text-white">Cancel</button>
          <button
            onClick={download}
            disabled={loading}
            className="text-sm px-5 py-2 bg-[#E6C982] text-[#0F1115] rounded-md font-semibold disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="studio-download-photos-go"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {loading ? "Zipping…" : "Download ZIP"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ----------------------------- Photos tab ----------------------------- */
const PhotosPanel = ({ story, password, onReload }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [order, setOrder] = useState(() => (story.photos || []).map((p) => p.id));
  const [showDownloadPhotos, setShowDownloadPhotos] = useState(false);
  const currentCount = story.photos?.length || 0;
  const remainingSlots = Math.max(0, MAX_STUDIO_PHOTOS - currentCount);

  // Sync order when parent story refreshes (upload / delete).
  useEffect(() => {
    setOrder((story.photos || []).map((p) => p.id));
  }, [(story.photos || []).map((p) => p.id).join(",")]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    if (remainingSlots <= 0) {
      toast.error(`Maximum ${MAX_STUDIO_PHOTOS} story photos`);
      return;
    }
    const allowedFiles = list.slice(0, remainingSlots);
    if (allowedFiles.length < list.length) {
      toast.error(`Only ${remainingSlots} more photo${remainingSlots === 1 ? "" : "s"} can be uploaded to this story.`);
    }
    setUploading(true);
    for (const f of allowedFiles) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        await axios.post(`${API}/studio/stories/${story.id}/photos`, fd, headers(password));
      } catch (e) {
        toast.error(`Upload failed: ${f.name}`);
      }
    }
    setUploading(false);
    onReload();
  };

  const deletePhoto = (photoId) =>
    axios.delete(`${API}/studio/stories/${story.id}/photos/${photoId}`, headers(password)).then(onReload);

  const onDragEnd = async (evt) => {
    const { active, over } = evt;
    if (!over || active.id === over.id) return;
    const oldIdx = order.indexOf(active.id);
    const newIdx = order.indexOf(over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const nextOrder = arrayMove(order, oldIdx, newIdx);
    setOrder(nextOrder);
    try {
      await axios.post(
        `${API}/studio/stories/${story.id}/photos/reorder`,
        { photo_ids: nextOrder },
        headers(password)
      );
      onReload();
    } catch {
      toast.error("Reorder failed");
    }
  };

  const byId = new Map((story.photos || []).map((p) => [p.id, p]));
  const ordered = order.map((id) => byId.get(id)).filter(Boolean);

  return (
    <div>
      {showDownloadPhotos && (
        <DownloadPhotosDialog
          storyId={story.id}
          password={password}
          photoCount={currentCount}
          favCount={(story.favorite_things || []).length}
          onClose={() => setShowDownloadPhotos(false)}
        />
      )}
      <div className="flex items-start justify-between mb-4 gap-4">
        <p className="text-sm text-white/60 max-w-2xl">
          Upload each photo, then paste a short paragraph explaining why it matters to the pet parent.
          Claude uses each photo + your paragraph to write one story page.{" "}
          <span className="text-white/40">Drag the grip handle to reorder. Up to {MAX_STUDIO_PHOTOS} photos per book.</span>
        </p>
        <div className="shrink-0 text-right space-y-2">
          <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-2" data-testid="studio-photo-count">
            {currentCount} / {MAX_STUDIO_PHOTOS} uploaded
          </div>
          <div className="flex gap-2">
            {currentCount > 0 && (
              <button
                onClick={() => setShowDownloadPhotos(true)}
                className="border border-[#1E232D] hover:border-[#E6C982]/50 text-white/70 hover:text-[#E6C982] rounded-md px-3 py-2 text-sm inline-flex items-center gap-2 transition"
                data-testid="studio-download-photos-btn"
                title="Download all uploaded photos as ZIP"
              >
                <FileDown className="w-4 h-4" /> Download all
              </button>
            )}
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading || currentCount >= MAX_STUDIO_PHOTOS}
              className="bg-[#E6C982] text-[#0F1115] rounded-md px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60 shrink-0"
              data-testid="studio-upload-trigger"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? "Uploading…" : currentCount >= MAX_STUDIO_PHOTOS ? "Limit reached" : "Upload photos"}
            </button>
          </div>
        </div>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="studio-upload-input"
        />
      </div>
      {!story.photos.length ? (
        <EmptyState label="No photos yet. Click Upload to start." icon={<ImageIcon />} />
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={order} strategy={rectSortingStrategy}>
            <div className="space-y-3" data-testid="studio-photo-grid">
              {ordered.map((p, idx) => (
                <SortablePhotoCard
                  key={p.id}
                  photo={p}
                  index={idx}
                  storyId={story.id}
                  password={password}
                  onDelete={() => deletePhoto(p.id)}
                  onRotated={onReload}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};

/** Thin DnD wrapper around the existing PhotoCard. */
function SortablePhotoCard(props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: props.photo.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : "auto",
    opacity: isDragging ? 0.85 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="relative">
      {/* Drag handle — overlaid in the top-left corner of the card */}
      <button
        {...attributes}
        {...listeners}
        className="absolute top-2 left-2 z-10 p-1.5 bg-black/60 hover:bg-black/90 rounded text-white/50 hover:text-white cursor-grab active:cursor-grabbing touch-none transition"
        aria-label="Drag to reorder"
        title="Drag to reorder"
        data-testid={`studio-photo-drag-${props.photo.id}`}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <PhotoCard {...props} />
    </div>
  );
}

const PhotoCard = ({ photo, index, storyId, password, onDelete, onRotated, apiSegment = "photos" }) => {
  const [caption, setCaption] = useState(photo.caption || "");
  const [saving, setSaving] = useState(false);
  const [rotating, setRotating] = useState(false);
  useEffect(() => { setCaption(photo.caption || ""); }, [photo.id, photo.caption]);

  const persist = async () => {
    if ((caption || "") === (photo.caption || "")) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/studio/stories/${storyId}/${apiSegment}/${photo.id}`,
        { caption },
        headers(password)
      );
      // Refetch the story so the parent's `story.photos[].caption` is
      // in sync with what we just wrote. Without this, remounting this
      // card (e.g. after tab-switch-and-back) would reset the textarea
      // via the useEffect above to the stale empty parent value.
      onRotated?.();
    } catch {
      toast.error("Failed to save caption");
    } finally {
      setSaving(false);
    }
  };

  const rotate = async (direction) => {
    setRotating(true);
    try {
      await axios.post(
        `${API}/studio/stories/${storyId}/${apiSegment}/${photo.id}/rotate`,
        { direction },
        { ...headers(password), timeout: 30000 }
      );
      toast.success("Photo rotated");
      onRotated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rotate failed");
    } finally {
      setRotating(false);
    }
  };

  return (
    <div className="border border-[#1E232D] rounded-lg overflow-hidden bg-[#161A22]" data-testid={`studio-photo-card-${photo.id}`}>
      <div className="flex">
        <div className="w-40 aspect-square bg-[#0F1115] flex-shrink-0 relative">
          <img
            src={photo.url + (photo.rotated_at ? `?v=${encodeURIComponent(photo.rotated_at)}` : "")}
            alt={photo.filename}
            className="w-full h-full object-cover"
          />
          {rotating && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-white/80" />
            </div>
          )}
        </div>
        <div className="flex-1 p-3 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-wider text-white/40">
              Photo {index + 1}
            </div>
            <div className="flex items-center gap-1">
              {saving && <span className="text-[10px] text-white/40 mr-1">Saving…</span>}
              <button
                onClick={() => rotate("left")}
                disabled={rotating}
                className="text-white/40 hover:text-white p-1 disabled:opacity-30"
                title="Rotate 90° counter-clockwise"
                data-testid={`studio-rotate-left-${photo.id}`}
              >
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => rotate("right")}
                disabled={rotating}
                className="text-white/40 hover:text-white p-1 disabled:opacity-30"
                title="Rotate 90° clockwise"
                data-testid={`studio-rotate-right-${photo.id}`}
              >
                <RotateCw className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => rotate("flip")}
                disabled={rotating}
                className="text-white/40 hover:text-white p-1 disabled:opacity-30"
                title="Flip 180° (fix upside-down)"
                data-testid={`studio-rotate-flip-${photo.id}`}
              >
                <FlipVertical className="w-3.5 h-3.5" />
              </button>
              <button onClick={onDelete} className="text-white/30 hover:text-red-300 p-1 ml-1" title="Delete">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <textarea
            rows={5}
            className={inputCls + " flex-1 text-xs min-h-[100px] resize-none"}
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            onBlur={persist}
            placeholder="Why is this photo special to the pet parent? Pets they're with, the moment, the season, anything that adds emotion to the story…"
            data-testid={`studio-photo-caption-${photo.id}`}
          />
        </div>
      </div>
    </div>
  );
};

/* ----------------------------- Favorite Toys & Things ----------------------------- */
// Reference photos used ONLY to generate empty-page backgrounds. Same
// data shape as `photos`, so we reuse PhotoCard by pointing its API
// segment at `favorite-things`.
const FavoriteThingsPanel = ({ story, password, onReload }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const items = story.favorite_things || [];

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true);
    for (const f of list) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        await axios.post(`${API}/studio/stories/${story.id}/favorite-things`, fd, headers(password));
      } catch (e) {
        toast.error(`Upload failed: ${f.name}`);
      }
    }
    setUploading(false);
    onReload();
  };
  const remove = (id) =>
    axios.delete(`${API}/studio/stories/${story.id}/favorite-things/${id}`, headers(password)).then(onReload);

  return (
    <div>
      <div className="flex items-start justify-between mb-4 gap-4">
        <p className="text-sm text-white/60 max-w-2xl">
          Upload 5–6 photos of the pet&rsquo;s favorite toys, treats, belongings, or other meaningful objects. Each description helps the AI understand what to illustrate. These are used <span className="text-white/80">only</span> as visual references when generating Empty Pages — they never become full story pages of their own.
        </p>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="bg-[#E6C982] text-[#0F1115] rounded-md px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60 shrink-0"
          data-testid="studio-favorites-upload-trigger"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? "Uploading…" : "Upload references"}
        </button>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="studio-favorites-upload-input"
        />
      </div>
      {!items.length ? (
        <EmptyState label="No reference photos yet. Upload the pet's favorite toys, treats, or belongings." icon={<ImageIcon />} />
      ) : (
        <>
          <div className="text-[11px] text-white/40 mb-3">
            {items.length} reference{items.length === 1 ? "" : "s"} · used as inputs when generating Empty Pages
          </div>
          <div className="grid grid-cols-2 gap-4" data-testid="studio-favorites-grid">
            {items.map((p, idx) => (
              <PhotoCard
                key={p.id}
                photo={p}
                index={idx}
                storyId={story.id}
                password={password}
                onDelete={() => remove(p.id)}
                onRotated={onReload}
                apiSegment="favorite-things"
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

/* ----------------------------- Empty Pages ----------------------------- */
// Keepsake-style background illustrations generated from the story's
// favorite_things references. Renders 3 pages per batch.
const EmptyPagesPanel = ({ story, password, onReload }) => {
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(null);
  const [expandedIds, setExpandedIds] = useState({});
  const [assembledPrompts, setAssembledPrompts] = useState({});
  const [overrideDrafts, setOverrideDrafts] = useState({});
  const [rawEditModes, setRawEditModes] = useState({});
  const [regenLoading, setRegenLoading] = useState({});
  const [collageBackgroundId, setCollageBackgroundId] = useState(null);
  const [collageLoading, setCollageLoading] = useState(false);
  const [collageProgress, setCollageProgress] = useState(null);

  const items = story.empty_pages || [];
  const collagePages = story.collage_pages || [];
  const refCount = (story.favorite_things || []).length;

  // Init drafts from story data
  useEffect(() => {
    const drafts = {};
    const modes = {};
    (story.empty_pages || []).forEach((ep) => {
      drafts[ep.id] = ep.full_prompt_override || "";
      modes[ep.id] = Boolean((ep.full_prompt_override || "").trim());
    });
    setOverrideDrafts(drafts);
    setRawEditModes(modes);
  }, [story.id, (story.empty_pages || []).map((e) => e.id + e.full_prompt_override).join(",")]);

  const loadPrompt = async (epId) => {
    if (assembledPrompts[epId]) return;
    try {
      const r = await axios.get(`${API}/studio/stories/${story.id}/empty-pages/${epId}/prompt-preview`, headers(password));
      setAssembledPrompts((prev) => ({ ...prev, [epId]: r.data.assembled_prompt || "" }));
    } catch { setAssembledPrompts((prev) => ({ ...prev, [epId]: "(Could not load)" })); }
  };

  const toggleExpand = (epId) => {
    const next = !expandedIds[epId];
    setExpandedIds((prev) => ({ ...prev, [epId]: next }));
    if (next) loadPrompt(epId);
  };

  const saveOverride = async (epId, value) => {
    try {
      await axios.patch(`${API}/studio/stories/${story.id}/empty-pages/${epId}`, { full_prompt_override: value }, headers(password));
      onReload();
    } catch { toast.error("Save failed"); }
  };

  const regenEp = async (epId) => {
    setRegenLoading((p) => ({ ...p, [epId]: true }));
    try {
      const r = await axios.post(`${API}/studio/stories/${story.id}/empty-pages/${epId}/regenerate`, {}, headers(password));
      const jobId = r.data?.job_id;
      if (!jobId) throw new Error("No job id");
      const job = await pollJob(jobId, password, { intervalMs: 4000, timeoutMs: 8 * 60 * 1000 });
      if (job.status !== "done") throw new Error(job.error || "Regeneration failed");
      toast.success("Empty page regenerated");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Regeneration failed");
    } finally { setRegenLoading((p) => ({ ...p, [epId]: false })); }
  };

  const generate = async () => {
    if (refCount === 0) { toast.error("Upload at least 1 photo in Favorite Toys & Things first"); return; }
    setGenerating(true);
    setProgress({ done: 0, total: 3, failed: 0 });
    try {
      const r = await axios.post(`${API}/studio/stories/${story.id}/empty-pages/generate`, {}, headers(password));
      const jobId = r.data?.job_id;
      if (!jobId) throw new Error("No job id returned");
      const job = await pollJob(jobId, password, {
        intervalMs: 4000, timeoutMs: 10 * 60 * 1000,
        onTick: (j) => j.result && setProgress({ done: j.result.done || 0, total: j.result.total || 3, failed: j.result.failed || 0 }),
      });
      if (job.status !== "done") throw new Error(job.error || "Generation failed");
      const { done, total, failed } = job.result || {};
      if (failed > 0) toast.warning(`Empty pages · ${done}/${total} succeeded, ${failed} failed`);
      else toast.success(`Generated ${done} empty page${done === 1 ? "" : "s"}`);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Empty pages generation failed");
    } finally { setGenerating(false); setProgress(null); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this empty page?")) return;
    await axios.delete(`${API}/studio/stories/${story.id}/empty-pages/${id}`, headers(password));
    onReload();
  };

  const selectVersion = async (epId, versionId) => {
    await axios.post(`${API}/studio/stories/${story.id}/empty-pages/${epId}/select-version?version_id=${versionId}`, {}, headers(password));
    onReload();
  };

  const generateCollage = async () => {
    if (!collageBackgroundId) { toast.error("Select an empty page as the collage background first"); return; }
    setCollageLoading(true);
    setCollageProgress({ done: 0, total: 3, failed: 0 });
    try {
      const r = await axios.post(
        `${API}/studio/stories/${story.id}/generate-collage-pages`,
        { empty_page_id: collageBackgroundId },
        headers(password)
      );
      const jobId = r.data?.job_id;
      if (!jobId) throw new Error("No job id");
      const job = await pollJob(jobId, password, {
        intervalMs: 3000, timeoutMs: 5 * 60 * 1000,
        onTick: (j) => j.result && setCollageProgress({ done: j.result.done || 0, total: 3, failed: j.result.failed || 0 }),
      });
      if (job.status !== "done") throw new Error(job.error || "Collage generation failed");
      toast.success("3 collage pages generated");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Collage generation failed");
    } finally { setCollageLoading(false); setCollageProgress(null); }
  };

  return (
    <div className="space-y-8">
      {/* ---- Empty pages section ---- */}
      <div>
        <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
          <div>
            <p className="text-sm text-white/60 max-w-2xl">
              Keepsake-style illustrated backgrounds from Favorite Toys & Things. Each leaves the centre open for photos and text.
              {refCount === 0 && <span className="text-[#E6C982]/80"> Upload references first →</span>}
            </p>
            {items.length > 0 && (
              <p className="text-xs text-white/40 mt-1">
                Select one as the background for photo collage pages below. Click a card to see/edit its prompt.
              </p>
            )}
          </div>
          <button
            onClick={generate}
            disabled={generating || refCount === 0}
            className="bg-[#E6C982] text-[#0F1115] rounded-md px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60 shrink-0"
            data-testid="studio-empty-pages-generate"
            title={refCount === 0 ? "Upload references in Favorite Toys & Things first" : "Render 3 empty-page variations"}
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            {generating
              ? (progress ? `Rendering ${progress.done}/${progress.total}…` : "Rendering…")
              : `Generate 3 empty pages${refCount ? ` · ${refCount} ref${refCount === 1 ? "" : "s"}` : ""}`}
          </button>
        </div>

        {!items.length ? (
          <EmptyState
            label={refCount === 0
              ? "Upload favorite toys / things first, then click Generate to render 3 empty pages."
              : "No empty pages yet. Click Generate to render 3 backgrounds."}
            icon={<BookOpen />}
          />
        ) : (
          <div className="space-y-4" data-testid="studio-empty-pages-grid">
            {items.map((ep, idx) => {
              const selected = (ep.versions || []).find((v) => v.id === ep.selected_version_id) || (ep.versions || [])[0];
              const isExpanded = expandedIds[ep.id];
              const isCollageSelected = collageBackgroundId === ep.id;
              const override = overrideDrafts[ep.id] ?? (ep.full_prompt_override || "");
              const isRawMode = rawEditModes[ep.id];
              const assembled = assembledPrompts[ep.id] || "";
              return (
                <div
                  key={ep.id}
                  className={`border rounded-lg overflow-hidden bg-[#161A22] transition ${isCollageSelected ? "border-[#E6C982]" : "border-[#1E232D]"}`}
                  data-testid={`studio-empty-page-${ep.id}`}
                >
                  {/* Card header: thumbnail + controls */}
                  <div className="flex gap-3 p-3">
                    {/* Thumbnail */}
                    <div className="shrink-0 w-40 aspect-[297/210] rounded overflow-hidden bg-[#0F1115]">
                      {selected?.url ? (
                        <img src={selected.url} alt={`Empty page ${idx + 1}`} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white/20 text-xs">(no render)</div>
                      )}
                    </div>
                    {/* Info + controls */}
                    <div className="flex-1 min-w-0 flex flex-col gap-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-medium text-white">Empty page {idx + 1}</div>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => regenEp(ep.id)}
                            disabled={regenLoading[ep.id]}
                            className="text-[11px] px-2 py-1 bg-[#1E232D] hover:bg-[#262B36] text-white rounded-md inline-flex items-center gap-1 disabled:opacity-60"
                            title="Regenerate with current prompt"
                            data-testid={`studio-empty-regen-${ep.id}`}
                          >
                            {regenLoading[ep.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                            Regen
                          </button>
                          <button
                            onClick={() => toggleExpand(ep.id)}
                            className="text-[11px] px-2 py-1 bg-[#1E232D] hover:bg-[#262B36] text-white rounded-md inline-flex items-center gap-1"
                            data-testid={`studio-empty-expand-${ep.id}`}
                          >
                            <Settings className="w-3 h-3" />
                            {isExpanded ? "Hide prompt" : "Edit prompt"}
                          </button>
                          <button
                            onClick={() => remove(ep.id)}
                            className="text-white/40 hover:text-red-300 p-1"
                            title="Delete"
                            data-testid={`studio-empty-page-delete-${ep.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>

                      {/* Version thumbnails */}
                      {(ep.versions || []).length > 1 && (
                        <div className="flex gap-1.5 flex-wrap">
                          {(ep.versions || []).map((v, vi) => (
                            <button
                              key={v.id}
                              onClick={() => selectVersion(ep.id, v.id)}
                              className={`relative w-12 aspect-[297/210] rounded overflow-hidden border transition ${ep.selected_version_id === v.id ? "border-[#E6C982]" : "border-[#1E232D] hover:border-white/30"}`}
                              title={`Version ${vi + 1}`}
                              data-testid={`studio-empty-version-${v.id}`}
                            >
                              <img src={v.url} alt="" className="w-full h-full object-cover" />
                              {ep.selected_version_id === v.id && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                                  <Check className="w-3 h-3 text-[#E6C982]" />
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Collage background selector */}
                      <label className={`flex items-center gap-2 cursor-pointer text-xs mt-auto ${isCollageSelected ? "text-[#E6C982]" : "text-white/50 hover:text-white/80"}`}>
                        <input
                          type="radio"
                          name="collage_bg"
                          checked={isCollageSelected}
                          onChange={() => setCollageBackgroundId(ep.id)}
                          className="accent-[#E6C982]"
                          data-testid={`studio-collage-select-${ep.id}`}
                        />
                        {isCollageSelected ? "Selected as collage background" : "Use as collage background"}
                      </label>
                    </div>
                  </div>

                  {/* Expanded prompt panel */}
                  {isExpanded && (
                    <div className="border-t border-[#1E232D] p-4 bg-[#0B0D12]">
                      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                        <div className="text-[11px] uppercase tracking-wider text-white/40">
                          Image-gen prompt
                          {(override || "").trim()
                            ? <span className="ml-2 text-[10px] text-[#E6C982]">Override active · sent verbatim</span>
                            : <span className="ml-2 text-[10px] text-white/30">Assembled from book settings</span>}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => loadPrompt(ep.id).then(() => setAssembledPrompts((p) => ({ ...p })))}
                            className="text-[11px] px-2 py-1 bg-[#1E232D] text-white rounded-md inline-flex items-center gap-1"
                          >
                            <RefreshCw className="w-3 h-3" /> Refresh
                          </button>
                          {!isRawMode ? (
                            <button
                              onClick={() => {
                                setRawEditModes((p) => ({ ...p, [ep.id]: true }));
                                if (!override) setOverrideDrafts((p) => ({ ...p, [ep.id]: assembled }));
                              }}
                              className="text-[11px] px-2 py-1 bg-[#1E232D] text-white rounded-md"
                            >Override raw…</button>
                          ) : (
                            <button
                              onClick={() => {
                                setRawEditModes((p) => ({ ...p, [ep.id]: false }));
                                setOverrideDrafts((p) => ({ ...p, [ep.id]: "" }));
                                saveOverride(ep.id, "");
                              }}
                              className="text-[11px] px-2 py-1 bg-[#1E232D] text-white rounded-md"
                            >Reset to assembled</button>
                          )}
                        </div>
                      </div>
                      {!isRawMode ? (
                        <pre className="text-[11px] font-mono text-white/70 whitespace-pre-wrap bg-[#0F1115] border border-[#1E232D] rounded p-3 max-h-48 overflow-y-auto">
                          {assembled || "(Loading…)"}
                        </pre>
                      ) : (
                        <textarea
                          rows={6}
                          className={inputCls + " text-[11px] font-mono leading-relaxed min-h-[140px]"}
                          value={override}
                          onChange={(e) => setOverrideDrafts((p) => ({ ...p, [ep.id]: e.target.value }))}
                          onBlur={() => saveOverride(ep.id, override)}
                          placeholder="Paste the exact prompt to send to the image model…"
                          data-testid={`studio-empty-prompt-override-${ep.id}`}
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ---- Collage pages section ---- */}
      <div>
        <div className="flex items-center justify-between mb-3 gap-4 flex-wrap">
          <div>
            <div className="text-sm font-medium text-white mb-0.5">Photo collage pages</div>
            <p className="text-xs text-white/50">
              Lay your raw photos organically onto the selected background. Photos keep their original aspect ratios and get a thick white border.
              {!collageBackgroundId && items.length > 0 && <span className="text-[#E6C982]/70"> Select an empty page above first.</span>}
            </p>
          </div>
          {items.length > 0 && (
            <button
              onClick={generateCollage}
              disabled={collageLoading || !collageBackgroundId || !(story.photos || []).length}
              className="bg-[#E6C982] text-[#0F1115] rounded-md px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60 shrink-0"
              data-testid="studio-generate-collage-btn"
              title={!collageBackgroundId ? "Select an empty page as background first" : "Generate 3 collage pages"}
            >
              {collageLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Layers className="w-4 h-4" />}
              {collageLoading
                ? (collageProgress ? `Compositing ${collageProgress.done}/3…` : "Compositing…")
                : `Generate 3 collage pages${(story.photos || []).length ? ` · ${(story.photos || []).length} photos` : ""}`}
            </button>
          )}
        </div>
        {collagePages.length === 0 ? (
          <div className="border border-dashed border-[#1E232D] rounded-lg p-8 text-center text-white/30 text-xs">
            {items.length === 0
              ? "Generate empty pages first, then select one as the collage background."
              : "Select an empty page as background and click Generate 3 collage pages."}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4" data-testid="studio-collage-pages-grid">
            {collagePages.map((cp, idx) => {
              const sel = (cp.versions || []).find((v) => v.id === cp.selected_version_id) || (cp.versions || [])[0];
              return (
                <div key={cp.id} className="border border-[#1E232D] rounded-lg overflow-hidden bg-[#161A22]" data-testid={`studio-collage-page-${cp.id}`}>
                  <div className="px-3 pt-3 pb-1 flex items-center justify-between">
                    <div className="text-xs text-white/60 font-medium">Collage page {idx + 1} · {cp.photo_count} photo{cp.photo_count !== 1 ? "s" : ""}</div>
                    {sel?.url && (
                      <button
                        onClick={() => downloadVersion(sel, story, `collage-${idx + 1}`)}
                        className="text-[11px] text-white/40 hover:text-[#E6C982] inline-flex items-center gap-1"
                        title="Download this collage page"
                      >
                        <Download className="w-3 h-3" /> Download
                      </button>
                    )}
                  </div>
                  {sel?.url ? (
                    <img src={sel.url} alt={`Collage ${idx + 1}`} className="w-full object-contain bg-[#0F1115] max-h-[500px]" />
                  ) : (
                    <div className="w-full aspect-[297/210] flex items-center justify-center text-white/20 text-xs">(empty batch)</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

/* ----------------------------- Page 17 (Love-note dedication) ----------------------------- */
// A single closing-page dedication: Claude condenses the pet notes +
// photo captions into 20–25 words, then the image model paints it as
// hand-lettered typography with decorative motifs from the pet's life.
const LovePagePanel = ({ story, password, onReload }) => {
  const love = story.love_page || {};
  const [textDraft, setTextDraft] = useState(love.text || "");
  const [notesDraft, setNotesDraft] = useState(love.notes || "");
  const [textGenLoading, setTextGenLoading] = useState(false);
  const [illuLoading, setIlluLoading] = useState(false);
  const [assembledPrompt, setAssembledPrompt] = useState("");
  const [overrideDraft, setOverrideDraft] = useState(love.full_prompt_override || "");
  const [rawEditMode, setRawEditMode] = useState(Boolean((love.full_prompt_override || "").trim()));

  useEffect(() => {
    setTextDraft(love.text || "");
    setNotesDraft(love.notes || "");
    setOverrideDraft(love.full_prompt_override || "");
    setRawEditMode(Boolean((love.full_prompt_override || "").trim()));
  }, [story.id, love.text, love.notes, love.full_prompt_override]);

  const loadAssembled = async () => {
    try {
      const r = await axios.get(
        `${API}/studio/stories/${story.id}/love-page/prompt-preview`,
        headers(password)
      );
      setAssembledPrompt(r.data?.assembled_prompt || "");
    } catch {
      setAssembledPrompt("(Could not load preview)");
    }
  };
  useEffect(() => { loadAssembled(); },
    [story.id, love.text, story.pet_notes, JSON.stringify(story.photos?.map(p => p.caption))]);

  const updateLove = async (patch) => {
    try {
      await axios.put(
        `${API}/studio/stories/${story.id}/love-page`,
        patch,
        headers(password)
      );
      onReload();
    } catch {
      toast.error("Save failed");
    }
  };

  const generateText = async () => {
    setTextGenLoading(true);
    try {
      const r = await axios.post(
        `${API}/studio/stories/${story.id}/love-page/generate-text`,
        {},
        headers(password)
      );
      const jobId = r.data?.job_id;
      if (!jobId) throw new Error("No job id");
      const job = await pollJob(jobId, password, { intervalMs: 3000, timeoutMs: 3 * 60 * 1000 });
      if (job.status !== "done") throw new Error(job.error || "Failed");
      toast.success("3 dedication options generated");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Text generation failed");
    } finally {
      setTextGenLoading(false);
    }
  };

  const illustrate = async () => {
    // Flush any dirty draft first so the render uses the latest text.
    const patch = {};
    if (textDraft !== (love.text || "")) patch.text = textDraft;
    if (notesDraft !== (love.notes || "")) patch.notes = notesDraft;
    if (overrideDraft !== (love.full_prompt_override || "")) patch.full_prompt_override = overrideDraft;
    if (Object.keys(patch).length) {
      await axios.put(`${API}/studio/stories/${story.id}/love-page`, patch, headers(password));
    }
    setIlluLoading(true);
    try {
      const r = await axios.post(
        `${API}/studio/stories/${story.id}/love-page/illustrate`,
        {},
        headers(password)
      );
      const jobId = r.data?.job_id;
      if (!jobId) throw new Error("No job id");
      const job = await pollJob(jobId, password, { intervalMs: 4000, timeoutMs: 5 * 60 * 1000 });
      if (job.status !== "done") throw new Error(job.error || "Failed");
      toast.success("Page 17 rendered");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Illustration failed");
    } finally {
      setIlluLoading(false);
    }
  };

  const selectVersion = (vid) =>
    axios.post(
      `${API}/studio/stories/${story.id}/love-page/select-version?version_id=${vid}`,
      {},
      headers(password)
    ).then(onReload);

  const wordCount = (textDraft || "").trim() ? textDraft.trim().split(/\s+/).length : 0;
  const wcColor = wordCount === 0 ? "text-white/30"
    : wordCount < 15 || wordCount > 30 ? "text-red-300"
    : wordCount >= 20 && wordCount <= 25 ? "text-[#E6C982]"
    : "text-white/60";
  const options = love.text_options || [];
  const selectedVersion = (love.versions || []).find(v => v.id === love.selected_version_id) || (love.versions || [])[0];

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <h2 className="text-2xl font-serif mb-1">Page 17 · Closing dedication</h2>
        <p className="text-sm text-white/50 max-w-2xl">
          A single quiet closing page. Claude condenses the pet notes + every photo caption into a 20–25 word dedication; the image model paints it as hand-lettered typography surrounded by decorative motifs drawn from your pet&rsquo;s life.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        {/* Left: text + prompt + generate controls */}
        <div className="space-y-4">
          <SectionCard title="Dedication text" hint="Aim for 20–25 words. Claude tightens for you — you can also just type your own.">
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <div className="text-[11px] uppercase tracking-wider text-white/40">
                Word count: <span className={`font-mono ${wcColor}`}>{wordCount}</span>
                <span className="ml-2 text-white/30">target 20–25</span>
              </div>
              <button
                onClick={generateText}
                disabled={textGenLoading}
                className="text-xs px-3 py-2 bg-[#1E232D] text-white rounded-md inline-flex items-center gap-1 disabled:opacity-60"
                data-testid="studio-love-generate-text"
              >
                {textGenLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {textGenLoading ? "Generating…" : "Generate 3 options with Claude"}
              </button>
            </div>
            <textarea
              rows={4}
              className={inputCls + " text-sm"}
              value={textDraft}
              onChange={(e) => setTextDraft(e.target.value)}
              onBlur={() => textDraft !== (love.text || "") && updateLove({ text: textDraft })}
              placeholder="Rex, from every wagging welcome to every quiet head on our knee — you turned this house into home."
              data-testid="studio-love-text"
            />
            {options.length > 0 && (
              <div className="mt-3 space-y-1.5">
                <div className="text-[10px] uppercase tracking-wider text-white/40">Claude&rsquo;s options · click to use</div>
                {options.map((o, i) => {
                  const active = textDraft.trim() === o.trim();
                  return (
                    <button
                      key={i}
                      onClick={() => { setTextDraft(o); updateLove({ text: o }); }}
                      className={`w-full text-left text-xs px-3 py-2 rounded border transition ${
                        active ? "border-[#E6C982] bg-[#E6C982]/5 text-white" : "border-[#1E232D] text-white/70 hover:border-white/30"
                      }`}
                      data-testid={`studio-love-text-option-${i}`}
                    >
                      <span className="text-[10px] text-white/30 mr-1">Option {i + 1}</span> {o}
                    </button>
                  );
                })}
              </div>
            )}
          </SectionCard>

          <SectionCard title="Admin notes (not sent to AI)" hint="Anything you want to remember about this page.">
            <textarea
              rows={2}
              className={inputCls + " text-xs"}
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              onBlur={() => notesDraft !== (love.notes || "") && updateLove({ notes: notesDraft })}
              placeholder="e.g., remember to hand-check word count before final print"
              data-testid="studio-love-notes"
            />
          </SectionCard>

          {/* Final image-gen prompt panel — mirrors PageCard */}
          <div className="border border-[#1E232D] rounded-md p-4 bg-[#0B0D12]">
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <div className="text-[11px] uppercase tracking-wider text-white/40">
                Final image-gen prompt
                {(love.full_prompt_override || "").trim() ? (
                  <span className="ml-2 text-[10px] text-[#E6C982]">Overridden · sent verbatim</span>
                ) : (
                  <span className="ml-2 text-[10px] text-white/30">Assembled from text + motifs above</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={loadAssembled} className="text-[11px] px-2 py-1 bg-[#1E232D] text-white rounded-md inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" />Refresh</button>
                {!rawEditMode ? (
                  <button
                    onClick={() => { setRawEditMode(true); if (!overrideDraft) setOverrideDraft(assembledPrompt); }}
                    className="text-[11px] px-2 py-1 bg-[#1E232D] text-white rounded-md"
                    data-testid="studio-love-enable-raw"
                  >Override raw…</button>
                ) : (
                  <button
                    onClick={() => {
                      setRawEditMode(false);
                      setOverrideDraft("");
                      if ((love.full_prompt_override || "")) updateLove({ full_prompt_override: "" });
                    }}
                    className="text-[11px] px-2 py-1 bg-[#1E232D] text-white rounded-md"
                    data-testid="studio-love-reset-raw"
                  >Reset to assembled</button>
                )}
              </div>
            </div>
            {!rawEditMode ? (
              <pre className="text-[11px] font-mono text-white/70 whitespace-pre-wrap bg-[#0F1115] border border-[#1E232D] rounded p-3 max-h-72 overflow-y-auto" data-testid="studio-love-assembled">
{assembledPrompt || "(Assembled prompt will appear once text is set.)"}
              </pre>
            ) : (
              <textarea
                rows={Math.min(16, Math.max(6, (overrideDraft || "").split("\n").length + 1))}
                className={inputCls + " min-h-[160px] text-[11px] font-mono leading-relaxed"}
                value={overrideDraft}
                onChange={(e) => setOverrideDraft(e.target.value)}
                onBlur={() => overrideDraft !== (love.full_prompt_override || "") && updateLove({ full_prompt_override: overrideDraft })}
                placeholder="Paste the exact string you want sent to Nano Banana / GPT Image 1…"
                data-testid="studio-love-full-prompt-override"
              />
            )}
          </div>

          <button
            onClick={illustrate}
            disabled={illuLoading || !textDraft.trim()}
            className="w-full bg-[#E6C982] text-[#0F1115] rounded-md px-4 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="studio-love-illustrate"
          >
            {illuLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            {illuLoading ? "Rendering Page 17…" : "Illustrate Page 17"}
          </button>
        </div>

        {/* Right: selected version + version list */}
        <div>
          <SectionCard title="Selected render" hint="Latest render is auto-selected. Click any thumbnail to switch.">
            {selectedVersion?.url ? (
              <img
                src={selectedVersion.url}
                alt="Page 17"
                className="w-full aspect-[297/210] object-cover rounded bg-[#0F1115]"
                data-testid="studio-love-selected-img"
              />
            ) : (
              <div className="w-full aspect-[297/210] flex items-center justify-center text-white/30 text-xs bg-[#0F1115] rounded">
                No render yet — write text + click Illustrate
              </div>
            )}
            {(love.versions || []).length > 1 && (
              <div className="mt-3 grid grid-cols-3 gap-2">
                {(love.versions || []).map((v) => (
                  <button
                    key={v.id}
                    onClick={() => selectVersion(v.id)}
                    className={`aspect-[297/210] overflow-hidden rounded border-2 transition ${
                      love.selected_version_id === v.id ? "border-[#E6C982]" : "border-transparent hover:border-white/30"
                    }`}
                    data-testid={`studio-love-version-${v.id}`}
                  >
                    <img src={v.url} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </SectionCard>
        </div>
      </div>
    </div>
  );
};

/* ----------------------------- Plan tab ----------------------------- */
const PlanPanel = ({ story, planning, runPlan, aiDecideOrder, setAiDecideOrder }) => {
  const photos = story.photos || [];
  const withCaption = photos.filter((p) => (p.caption || "").trim().length > 0).length;
  return (
    <div className="max-w-2xl">
      <div className="bg-[#161A22] border border-[#1E232D] rounded-xl p-6">
        <div className="flex items-center gap-2 text-[#E6C982] mb-2">
          <Sparkles className="w-4 h-4" />
          <span className="text-xs uppercase tracking-[0.25em]">AI Story Planner</span>
        </div>
        <h2 className="text-xl font-serif mb-2">Generate one page per photo</h2>
        <p className="text-sm text-white/60 mb-4">
          Claude reads each photo alongside your paragraph for it and writes
          ONE story page per photo, with 2–3 short text options and a mood direction.
        </p>
        <ul className="text-xs text-white/50 space-y-1 mb-5">
          <li>· {photos.length} photos uploaded {photos.length ? "✓" : "(upload at least 1 to begin)"}</li>
          <li>· {withCaption} of {photos.length} have a paragraph {withCaption === photos.length && photos.length > 0 ? "✓" : "(empty paragraphs are okay — Claude will infer from the image)"}</li>
          <li>· Existing pages: {story.pages?.length ? `${story.pages.length} (will be replaced)` : "none"}</li>
        </ul>

        {/* Photo order option */}
        <div className="mb-5 p-3 rounded-lg border border-[#1E232D] bg-[#0B0D12]">
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2.5">Photo page order</div>
          <div className="flex flex-col gap-2">
            {[
              { v: false, l: "Keep customer's photo order", s: "Pages follow the order set on the /curate link. Recommended." },
              { v: true, l: "Let AI decide the best order", s: "Claude chooses chronological / emotional order. May reorder pages." },
            ].map((opt) => (
              <label key={String(opt.v)} className={`flex items-start gap-2.5 p-2.5 rounded border cursor-pointer transition ${aiDecideOrder === opt.v ? "border-[#E6C982] bg-[#E6C982]/5" : "border-[#1E232D] hover:border-white/20"}`}>
                <input type="radio" name="ai_order" checked={aiDecideOrder === opt.v} onChange={() => setAiDecideOrder(opt.v)} className="mt-0.5 accent-[#E6C982]" data-testid={`studio-order-option-${opt.v}`} />
                <div>
                  <div className="text-xs font-medium text-white">{opt.l}</div>
                  <div className="text-[11px] text-white/50 mt-0.5">{opt.s}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <button
          onClick={runPlan}
          disabled={planning || !photos.length}
          className="bg-[#E6C982] text-[#0F1115] rounded-md px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
          data-testid="studio-run-plan-btn"
        >
          {planning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
          {planning ? "Reading photos & writing…" : "Run AI Story Planner"}
        </button>
        <p className="text-[11px] text-white/40 mt-3">This usually takes 30–90 seconds for 10–20 photos.</p>
      </div>
    </div>
  );
};

/* ----------------------------- Pages tab ----------------------------- */
const PagesPanel = ({ story, password, onReload }) => {
  const [expandedId, setExpandedId] = useState(story.pages?.[0]?.id || null);
  const [batchTextLoading, setBatchTextLoading] = useState(false);
  const [batchImgLoading, setBatchImgLoading] = useState(false);
  const [batchImgProgress, setBatchImgProgress] = useState(null);
  const pages = story.pages || [];

  const reorder = async (idx, dir) => {
    const newOrder = [...pages.map((p) => p.id)];
    const swap = idx + dir;
    if (swap < 0 || swap >= newOrder.length) return;
    [newOrder[idx], newOrder[swap]] = [newOrder[swap], newOrder[idx]];
    await axios.post(`${API}/studio/stories/${story.id}/pages/reorder`, { page_ids: newOrder }, headers(password));
    onReload();
  };

  const addBlank = async () => {
    await axios.post(`${API}/studio/stories/${story.id}/pages`, {}, headers(password));
    onReload();
  };

  const deletePage = async (pid) => {
    if (!confirm("Delete this page?")) return;
    await axios.delete(`${API}/studio/stories/${story.id}/pages/${pid}`, headers(password));
    onReload();
  };

  const generateAllText = async () => {
    const anyTextExists = pages.some((p) => (p.text || "").trim() || (p.text_options || []).length);
    if (anyTextExists && !confirm(
      "This will overwrite existing page text on every page with a fresh, flow-aware pass from Claude. Continue?"
    )) return;
    setBatchTextLoading(true);
    try {
      const r = await axios.post(`${API}/studio/stories/${story.id}/generate-all-text`, {}, headers(password));
      const jobId = r.data?.job_id;
      if (!jobId) throw new Error("No job id returned");
      const job = await pollJob(jobId, password);
      if (job.status !== "done") throw new Error(job.error || "Text generation failed");
      const upd = job.result?.pages_updated || 0;
      const tot = job.result?.pages_total || pages.length;
      toast.success(`Text generated · ${upd}/${tot} pages updated`);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Batch text failed");
    } finally {
      setBatchTextLoading(false);
    }
  };

  const generateAllIllustrations = async () => {
    const withImages = pages.filter((p) => (p.versions || []).length > 0).length;
    if (withImages > 0 && !confirm(
      `${withImages} of ${pages.length} pages already have illustrations. Generating for all will ADD a new version to every page (existing ones stay). Continue?`
    )) return;
    setBatchImgLoading(true);
    setBatchImgProgress({ done: 0, total: pages.length, failed: 0 });
    try {
      const r = await axios.post(`${API}/studio/stories/${story.id}/generate-all-illustrations`, {}, headers(password));
      const jobId = r.data?.job_id;
      if (!jobId) throw new Error("No job id returned");
      const job = await pollJob(jobId, password, {
        intervalMs: 4000,
        timeoutMs: 30 * 60 * 1000, // 30 min — 20 pages × ~30–60s each
        onTick: (j) => {
          if (j.result) setBatchImgProgress({
            done: j.result.done || 0,
            total: j.result.total || pages.length,
            failed: j.result.failed || 0,
          });
        },
      });
      if (job.status !== "done") throw new Error(job.error || "Batch illustrate failed");
      const { done, total, failed } = job.result || {};
      if (failed > 0) toast.warning(`Batch done · ${done}/${total} succeeded, ${failed} failed`);
      else toast.success(`All ${done}/${total} illustrations generated`);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Batch illustrate failed");
    } finally {
      setBatchImgLoading(false);
      setBatchImgProgress(null);
    }
  };

  if (!pages.length) {
    return <EmptyState label="No pages yet. Go to Plan → Run AI Story Planner." icon={<BookOpen />} />;
  }
  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <p className="text-sm text-white/60">
          {pages.length} pages · click any card to edit.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={generateAllText}
            disabled={batchTextLoading || batchImgLoading}
            className="text-xs px-3 py-2 bg-[#E6C982] text-[#0F1115] rounded-md inline-flex items-center gap-1 font-semibold disabled:opacity-60"
            title="Ask Claude to write text for every page at once, keeping story flow coherent"
            data-testid="studio-generate-all-text"
          >
            {batchTextLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            Generate text for all pages
          </button>
          <button
            onClick={generateAllIllustrations}
            disabled={batchTextLoading || batchImgLoading}
            className="text-xs px-3 py-2 bg-[#E6C982] text-[#0F1115] rounded-md inline-flex items-center gap-1 font-semibold disabled:opacity-60"
            title="Generate illustrations for every page in one batch"
            data-testid="studio-generate-all-illustrations"
          >
            {batchImgLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
            Generate images for all pages
            {batchImgProgress && batchImgLoading && (
              <span className="ml-1 text-[10px] opacity-70">
                {batchImgProgress.done}/{batchImgProgress.total}
              </span>
            )}
          </button>
          <button
            onClick={addBlank}
            disabled={batchTextLoading || batchImgLoading}
            className="text-xs px-3 py-2 bg-[#1E232D] text-white rounded-md inline-flex items-center gap-1 disabled:opacity-60"
            data-testid="studio-add-blank-page"
          >
            <PlusCircle className="w-3.5 h-3.5" /> Add blank page
          </button>
        </div>
      </div>
      <p className="text-[11px] text-white/40 mb-4">
        Tip: reorder photos first (arrows on each card), then click <span className="text-[#E6C982]">Generate text for all pages</span> so page-to-page copy flows naturally. Once you are happy with text + directions, hit <span className="text-[#E6C982]">Generate images for all pages</span> to render the book in one shot.
      </p>
      <div className="space-y-3">
        {pages.map((page, idx) => (
          <PageCard
            key={page.id}
            story={story}
            page={page}
            idx={idx}
            total={pages.length}
            password={password}
            expanded={expandedId === page.id}
            onToggle={() => setExpandedId(expandedId === page.id ? null : page.id)}
            onReorder={(dir) => reorder(idx, dir)}
            onDelete={() => deletePage(page.id)}
            onReload={onReload}
          />
        ))}
      </div>
    </div>
  );
};

/* ----------------------------- UploadVariantButton ----------------------------- */
const UploadVariantButton = ({ storyId, targetId, role, password, onReload, testId, label = "Upload external variant" }) => {
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const endpoint = role === "page"
        ? `${API}/studio/stories/${storyId}/pages/${targetId}/upload-variant`
        : `${API}/studio/stories/${storyId}/cover/${role}/upload-variant`;
      await axios.post(endpoint, fd, { ...headers(password) });
      toast.success("Variant uploaded and selected");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  return (
    <>
      <button
        onClick={() => fileRef.current?.click()}
        disabled={loading}
        className="text-[10px] px-2 py-1 bg-[#1E232D] hover:bg-[#262B36] text-white rounded-md inline-flex items-center gap-1 disabled:opacity-60"
        data-testid={testId || `studio-upload-variant-${targetId}`}
        title="Upload an externally-generated image as a new variant"
      >
        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
        {label}
      </button>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
    </>
  );
};

const PageCard = ({ story, page, idx, total, password, expanded, onToggle, onReorder, onDelete, onReload }) => {
  const photo = useMemo(
    () => (story.photos || []).find((p) => p.id === page.photo_id),
    [story.photos, page.photo_id]
  );
  const [textDraft, setTextDraft] = useState(page.text || "");
  const [overrideDraft, setOverrideDraft] = useState(page.prompt_overrides || "");
  const [directionDraft, setDirectionDraft] = useState(page.illustration_direction || "");
  const [directionLoading, setDirectionLoading] = useState(false);
  const [direction, setDirection] = useState("");
  // Final image-gen prompt preview + raw-override editor. The assembled
  // string comes straight from the backend so what the admin sees is
  // exactly what the image model will receive.
  const [assembledPrompt, setAssembledPrompt] = useState("");
  const [assembledLoading, setAssembledLoading] = useState(false);
  const [fullPromptDraft, setFullPromptDraft] = useState(page.full_prompt_override || "");
  const [rawEditMode, setRawEditMode] = useState(false);
  const [editPrompt, setEditPrompt] = useState("");
  const [regenLoading, setRegenLoading] = useState(false);
  const [illuLoading, setIlluLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoFileRef = useRef(null);
  // Editable caption of the linked source photo, synced two-way with the
  // Photos tab. Persists via the existing PUT /photos/{pid} endpoint.
  const [photoCaptionDraft, setPhotoCaptionDraft] = useState(photo?.caption || "");
  const [photoCaptionSaving, setPhotoCaptionSaving] = useState(false);
  useEffect(() => {
    setPhotoCaptionDraft(photo?.caption || "");
  }, [photo?.id, photo?.caption]);

  const savePhotoCaption = async () => {
    if (!photo) return;
    if ((photoCaptionDraft || "") === (photo.caption || "")) return;
    setPhotoCaptionSaving(true);
    try {
      await axios.put(
        `${API}/studio/stories/${story.id}/photos/${photo.id}`,
        { caption: photoCaptionDraft },
        headers(password)
      );
      // Refetch so the Photos tab + the parent's story state also see the
      // updated caption (mirrors the fix from PhotoCard.persist()).
      onReload();
    } catch {
      toast.error("Failed to save photo description");
    } finally {
      setPhotoCaptionSaving(false);
    }
  };

  const uploadPagePhoto = async (file) => {
    if (!file) return;
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(
        `${API}/studio/stories/${story.id}/pages/${page.id}/photo`,
        fd,
        { ...headers(password), timeout: 60000 }
      );
      toast.success("Photo linked to page");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setPhotoUploading(false);
      if (photoFileRef.current) photoFileRef.current.value = "";
    }
  };
  // Per-generation provider override for this one click. `null` = use the
  // story's default provider set.
  const [providerOverride, setProviderOverride] = useState(null);
  useEffect(() => {
    setTextDraft(page.text || "");
    setOverrideDraft(page.prompt_overrides || "");
    setDirectionDraft(page.illustration_direction || "");
    setFullPromptDraft(page.full_prompt_override || "");
    setRawEditMode(Boolean((page.full_prompt_override || "").trim()));
  }, [page.id, page.text, page.prompt_overrides, page.illustration_direction, page.full_prompt_override]);

  const loadAssembledPrompt = async () => {
    if (!expanded) return;
    setAssembledLoading(true);
    try {
      const r = await axios.get(
        `${API}/studio/stories/${story.id}/pages/${page.id}/prompt-preview`,
        headers(password)
      );
      setAssembledPrompt(r.data?.assembled_prompt || "");
    } catch {
      // Non-fatal — show a placeholder.
      setAssembledPrompt("(Could not load preview)");
    } finally {
      setAssembledLoading(false);
    }
  };
  useEffect(() => {
    // Re-fetch the assembled prompt whenever the inputs that feed it
    // change (or the card is expanded). Keeps the preview in sync with
    // whatever the admin last saved.
    if (expanded) loadAssembledPrompt();
  }, [
    expanded, page.id, page.text, page.prompt_overrides,
    page.illustration_direction, page.illustration_prompt,
    story.custom_style_prompt, story.custom_layout_instructions,
    JSON.stringify(story.custom_prompts || {}),
  ]);

  const selected = (page.versions || []).find((v) => v.id === page.selected_version_id)
    || (page.versions || [])[(page.versions || []).length - 1];

  const updatePage = async (patch) => {
    await axios.put(`${API}/studio/stories/${story.id}/pages/${page.id}`, patch, headers(password));
    onReload();
  };

  const regenText = async () => {
    setRegenLoading(true);
    try {
      const r = await axios.post(
        `${API}/studio/stories/${story.id}/pages/${page.id}/regenerate-text`,
        { direction: direction || null, count: 3 },
        { ...headers(password), timeout: 60000 }
      );
      toast.success("New text options added");
      // Patch in place so the carousel updates without a full reload.
      page.text_options = r.data.text_options || [];
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Text regen failed");
    } finally {
      setRegenLoading(false);
    }
  };

  const regenDirection = async () => {
    if (!page.photo_id) {
      toast.error("Link a photo to this page first");
      return;
    }
    setDirectionLoading(true);
    try {
      const r = await axios.post(
        `${API}/studio/stories/${story.id}/pages/${page.id}/regenerate-direction`,
        {},
        { ...headers(password), timeout: 90000 }
      );
      const next = r.data?.illustration_direction || "";
      setDirectionDraft(next);
      // Patch in place so the next Generate uses the new direction even
      // before the parent reloads.
      page.illustration_direction = next;
      toast.success("Illustration direction refreshed");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Direction generation failed");
    } finally {
      setDirectionLoading(false);
    }
  };

  const illustrate = async () => {
    setIlluLoading(true);
    try {
      // Save text/overrides/direction first so the illustration uses the
      // latest values.
      const dirty = {};
      if (textDraft !== page.text) dirty.text = textDraft;
      if (overrideDraft !== page.prompt_overrides) dirty.prompt_overrides = overrideDraft;
      if (directionDraft !== (page.illustration_direction || "")) dirty.illustration_direction = directionDraft;
      if (fullPromptDraft !== (page.full_prompt_override || "")) dirty.full_prompt_override = fullPromptDraft;
      if (Object.keys(dirty).length) {
        await axios.put(
          `${API}/studio/stories/${story.id}/pages/${page.id}`,
          dirty,
          headers(password)
        );
      }
      // Async job: API returns 202 + job_id; poll until done.
      // If admin picked a one-off provider override, send it; else backend
      // uses the story's default provider set.
      const payload = providerOverride ? { providers: [providerOverride] } : {};
      const r = await axios.post(
        `${API}/studio/stories/${story.id}/pages/${page.id}/illustrate`,
        payload,
        headers(password)
      );
      const jobId = r.data?.job_id;
      if (!jobId) throw new Error("No job id returned");
      const job = await pollJob(jobId, password);
      if (job.status !== "done") throw new Error(job.error || "Generation failed");
      toast.success("Illustration generated");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Generation failed");
    } finally {
      setIlluLoading(false);
    }
  };

  const editIllustration = async () => {
    if (!editPrompt.trim()) return;
    setEditLoading(true);
    try {
      const r = await axios.post(
        `${API}/studio/stories/${story.id}/pages/${page.id}/edit-illustration`,
        { edit_prompt: editPrompt.trim() },
        headers(password)
      );
      const jobId = r.data?.job_id;
      if (!jobId) throw new Error("No job id returned");
      const job = await pollJob(jobId, password);
      if (job.status !== "done") throw new Error(job.error || "Edit failed");
      setEditPrompt("");
      toast.success("Edited variant created");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Edit failed");
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <div className="border border-[#1E232D] rounded-xl overflow-hidden" data-testid={`studio-page-card-${page.id}`}>
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onToggle())}
        className="w-full flex items-center gap-4 px-4 py-3 bg-[#161A22] hover:bg-[#1A1F29] cursor-pointer"
      >
        <div className="w-14 h-14 rounded-md bg-[#0F1115] overflow-hidden flex-shrink-0 flex items-center justify-center">
          {selected ? (
            <img src={selected.url} alt="" className="w-full h-full object-cover" />
          ) : photo ? (
            <img src={photo.url} alt="" className="w-full h-full object-cover opacity-50" />
          ) : (
            <ImageIcon className="w-5 h-5 text-white/30" />
          )}
        </div>
        <div className="flex-1 text-left min-w-0">
          <div className="text-xs text-white/40 uppercase tracking-wide">Page {idx + 1}{page.mood ? ` · ${page.mood}` : ""}</div>
          <div className="text-sm text-white/90 line-clamp-1">{page.text || page.title || <span className="text-white/40">(no text yet)</span>}</div>
        </div>
        <div className="flex items-center gap-1 text-white/40">
          <ChevronControl onClick={(e) => { e.stopPropagation(); onReorder(-1); }} disabled={idx === 0}><ArrowUp className="w-3.5 h-3.5" /></ChevronControl>
          <ChevronControl onClick={(e) => { e.stopPropagation(); onReorder(1); }} disabled={idx === total - 1}><ArrowDown className="w-3.5 h-3.5" /></ChevronControl>
          <ChevronControl onClick={(e) => { e.stopPropagation(); onDelete(); }}><Trash2 className="w-3.5 h-3.5" /></ChevronControl>
        </div>
      </div>
      {expanded && (
        <div className="p-5 bg-[#0F1115] space-y-5">
          <div className="grid grid-cols-2 gap-6">
          {/* Left: reference + text */}
          <div className="space-y-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-white/40 mb-2 flex items-center justify-between gap-2">
                <span>Source photo</span>
                <button
                  onClick={() => photoFileRef.current?.click()}
                  disabled={photoUploading}
                  className="text-[10px] px-2 py-1 bg-[#1E232D] text-white rounded-md inline-flex items-center gap-1 disabled:opacity-60"
                  data-testid={`studio-page-photo-upload-${page.id}`}
                  title={photo ? "Replace the linked photo" : "Upload a photo for this blank page"}
                >
                  {photoUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                  {photo ? "Replace" : "Upload"}
                </button>
                <input
                  ref={photoFileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => uploadPagePhoto(e.target.files?.[0])}
                  data-testid={`studio-page-photo-upload-input-${page.id}`}
                />
              </div>
              {photo ? (
                <div className="space-y-2">
                  <img src={photo.url + (photo.rotated_at ? `?v=${encodeURIComponent(photo.rotated_at)}` : "")} alt="" className="rounded-md border border-[#1E232D] max-h-48 object-contain" />
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1 flex items-center gap-2">
                      Photo description (why it&rsquo;s special)
                      {photoCaptionSaving && (
                        <span className="text-white/40 normal-case tracking-normal">· Saving…</span>
                      )}
                    </div>
                    <textarea
                      rows={3}
                      className={inputCls + " min-h-[70px] text-xs"}
                      value={photoCaptionDraft}
                      onChange={(e) => setPhotoCaptionDraft(e.target.value)}
                      onBlur={savePhotoCaption}
                      placeholder="Why is this photo special? (Same field as the Photos tab caption.)"
                      data-testid={`studio-page-photo-caption-${page.id}`}
                    />
                    <p className="text-[10px] text-white/30 mt-1">
                      Two-way synced with the Photos tab · used by the AI when writing text + illustration direction
                    </p>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => photoFileRef.current?.click()}
                  disabled={photoUploading}
                  className="w-full border-2 border-dashed border-[#1E232D] hover:border-[#E6C982] rounded-md p-6 flex flex-col items-center justify-center gap-2 text-xs text-white/40 hover:text-white transition disabled:opacity-60"
                  data-testid={`studio-page-photo-dropzone-${page.id}`}
                >
                  {photoUploading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Uploading…
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      Click to upload a photo for this blank page
                    </>
                  )}
                </button>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-white/40 mb-2">Page text (baked into illustration)</div>
              <textarea
                rows={4}
                className={inputCls + " min-h-[110px]"}
                value={textDraft}
                onChange={(e) => setTextDraft(e.target.value)}
                onBlur={() => textDraft !== page.text && updatePage({ text: textDraft })}
                data-testid={`studio-page-text-${page.id}`}
              />
              {(page.text_options || []).length > 0 && (
                <div className="mt-2 space-y-1.5">
                  <div className="text-[11px] uppercase tracking-wider text-white/40">Text options · click to use</div>
                  {(page.text_options || []).map((opt, i) => (
                    <button
                      key={i}
                      onClick={() => { setTextDraft(opt); updatePage({ text: opt }); }}
                      className={`w-full text-left text-xs px-3 py-2 rounded border border-[#1E232D] hover:border-[#E6C982] ${textDraft === opt ? "bg-[#1E232D] text-white" : "text-white/70"}`}
                      data-testid={`studio-page-text-option-${page.id}-${i}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
              <div className="mt-3 flex gap-2">
                <input
                  className={inputCls + " flex-1 text-xs"}
                  value={direction}
                  onChange={(e) => setDirection(e.target.value)}
                  placeholder='Direction (optional): "make it funnier", "shorter"…'
                />
                <button onClick={regenText} disabled={regenLoading} className="text-xs px-3 py-2 bg-[#1E232D] text-white rounded-md inline-flex items-center gap-1 disabled:opacity-60" data-testid={`studio-regen-text-${page.id}`}>
                  {regenLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Regen
                </button>
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-white/40 mb-2">Per-page prompt notes (overrides)</div>
              <textarea
                rows={3}
                className={inputCls + " min-h-[90px] text-xs"}
                value={overrideDraft}
                onChange={(e) => setOverrideDraft(e.target.value)}
                onBlur={() => overrideDraft !== page.prompt_overrides && updatePage({ prompt_overrides: overrideDraft })}
                placeholder="e.g., emphasise sunset, add lanterns…"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-wider text-white/40">
                  Illustration direction
                  {(page.illustration_direction || "").trim() ? (
                    <span className="ml-2 text-[10px] text-[#E6C982]">Ready · concatenated into image prompt</span>
                  ) : (
                    <span className="ml-2 text-[10px] text-white/30">Empty · auto-generated on Generate if left blank</span>
                  )}
                </div>
                <button
                  onClick={regenDirection}
                  disabled={directionLoading || !page.photo_id}
                  className="text-[11px] px-2.5 py-1 bg-[#1E232D] text-white rounded-md inline-flex items-center gap-1 disabled:opacity-60"
                  title="Ask Claude to (re)write the art-director briefing based on this photo + description"
                  data-testid={`studio-regen-direction-${page.id}`}
                >
                  {directionLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  {page.illustration_direction ? "Regenerate" : "Generate"}
                </button>
              </div>
              <textarea
                rows={8}
                className={inputCls + " min-h-[180px] text-xs font-mono leading-relaxed"}
                value={directionDraft}
                onChange={(e) => setDirectionDraft(e.target.value)}
                onBlur={() => directionDraft !== (page.illustration_direction || "") && updatePage({ illustration_direction: directionDraft })}
                placeholder={"### Illustration Direction\n\n- warm festive lights\n- quiet emotional page\n- soft watercolor florals\n- dreamy childhood nostalgia\n\n(Click Generate to have Claude write this from the photo + description.)"}
                data-testid={`studio-direction-${page.id}`}
              />
            </div>
          </div>

          {/* Right: illustration */}
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] uppercase tracking-wider text-white/40">Illustration</div>
              <div className="flex items-center gap-2">
                <ProviderSelect
                  value={providerOverride}
                  storyProviders={story.image_providers || []}
                  onChange={setProviderOverride}
                  testId={`studio-provider-select-${page.id}`}
                />
                <button
                  onClick={illustrate}
                  disabled={illuLoading}
                  className="bg-[#E6C982] text-[#0F1115] rounded-md px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60"
                  data-testid={`studio-illustrate-${page.id}`}
                >
                  {illuLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                  {selected ? "Regenerate" : "Generate"}
                </button>
              </div>
            </div>
            {selected ? (
              <div className="relative group">
                <img src={selected.url} alt="" className="rounded-md border border-[#1E232D] w-full object-contain bg-black" data-testid={`studio-page-illustration-${page.id}`} />
                {selected.provider && (
                  <div className="absolute top-2 left-2 bg-black/70 text-[10px] text-white px-2 py-0.5 rounded-full">
                    {providerLabel(selected.provider)}
                  </div>
                )}
                <button
                  onClick={() => downloadVersion(selected, story, idx + 1)}
                  className="absolute top-2 right-2 bg-black/70 hover:bg-black text-white text-[10px] px-2 py-1 rounded inline-flex items-center gap-1"
                  data-testid={`studio-download-${selected.id}`}
                  title="Download PNG"
                >
                  <Download className="w-3 h-3" /> Download
                </button>
              </div>
            ) : (
              <div className="border border-dashed border-[#1E232D] rounded-md p-6 flex flex-col items-center gap-3 text-white/40 text-xs text-center">
                <div>No illustration yet. Click Generate or upload an externally-created image.</div>
                <UploadVariantButton
                  storyId={story.id}
                  targetId={page.id}
                  role="page"
                  password={password}
                  onReload={onReload}
                  testId={`studio-upload-variant-empty-${page.id}`}
                  label="Upload variant"
                />
              </div>
            )}
            {selected && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    className={inputCls + " flex-1 text-xs"}
                    value={editPrompt}
                    onChange={(e) => setEditPrompt(e.target.value)}
                    placeholder='Edit: "remove background clutter", "move text to top"…'
                    data-testid={`studio-edit-prompt-${page.id}`}
                  />
                  <button onClick={editIllustration} disabled={editLoading || !editPrompt.trim()} className="text-xs px-3 py-2 bg-[#1E232D] text-white rounded-md inline-flex items-center gap-1 disabled:opacity-60" data-testid={`studio-apply-edit-${page.id}`}>
                    {editLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Edit
                  </button>
                </div>
                {(page.versions || []).length > 1 && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-[11px] uppercase tracking-wider text-white/40">All versions ({page.versions.length})</div>
                      <UploadVariantButton
                        storyId={story.id}
                        targetId={page.id}
                        role="page"
                        password={password}
                        onReload={onReload}
                        testId={`studio-upload-variant-${page.id}`}
                      />
                    </div>
                    <div className="grid grid-cols-5 gap-1.5">
                      {(page.versions || []).map((v, vi) => (
                        <div key={v.id} className="relative group">
                          <button
                            onClick={() => updatePage({ selected_version_id: v.id })}
                            className={`aspect-square w-full rounded-sm overflow-hidden border ${page.selected_version_id === v.id ? "border-[#E6C982]" : "border-[#1E232D]"}`}
                            title={v.edit_prompt || "original"}
                            data-testid={`studio-version-thumb-${v.id}`}
                          >
                            <img src={v.url} alt="" className="w-full h-full object-cover" />
                            {page.selected_version_id === v.id && (
                              <div className="absolute inset-0 flex items-end justify-end p-1 pointer-events-none">
                                <div className="bg-[#E6C982] rounded-full p-0.5">
                                  <Check className="w-2.5 h-2.5 text-[#0F1115]" />
                                </div>
                              </div>
                            )}
                          </button>
                          {v.provider && (
                            <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-[9px] text-white/90 px-1 py-0.5 truncate text-center">
                              v{vi + 1} · {providerLabel(v.provider)}
                            </div>
                          )}
                          <button
                            onClick={() => downloadVersion(v, story, idx + 1)}
                            className="absolute top-0 right-0 bg-black/70 hover:bg-black text-white p-0.5 opacity-0 group-hover:opacity-100 transition"
                            title="Download"
                          >
                            <Download className="w-2.5 h-2.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          </div>

          {/* Customer preview feedback */}
          {(page.preview_feedback || "").trim() && (
            <div className="border border-[#E6C982]/30 bg-[#E6C982]/5 rounded-md p-3">
              <div className="text-[10px] uppercase tracking-wider text-[#E6C982]/70 mb-1 flex items-center gap-1.5">
                <span>Customer feedback</span>
              </div>
              <p className="text-xs text-white/80 whitespace-pre-wrap">{page.preview_feedback}</p>
            </div>
          )}

          {/* Full-width: final assembled prompt (read-only preview + raw override) */}
          <div className="border border-[#1E232D] rounded-md p-4 bg-[#0B0D12]">
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <div className="text-[11px] uppercase tracking-wider text-white/40">
                Final image-gen prompt
                {(page.full_prompt_override || "").trim() ? (
                  <span className="ml-2 text-[10px] text-[#E6C982]">Overridden · sent verbatim to the image model</span>
                ) : (
                  <span className="ml-2 text-[10px] text-white/30">
                    Assembled from Setup + fields above{assembledLoading ? " · refreshing…" : ""}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={loadAssembledPrompt}
                  disabled={assembledLoading}
                  className="text-[11px] px-2 py-1 bg-[#1E232D] text-white rounded-md inline-flex items-center gap-1 disabled:opacity-60"
                  title="Refresh the assembled preview"
                  data-testid={`studio-refresh-assembled-${page.id}`}
                >
                  {assembledLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  Refresh
                </button>
                {!rawEditMode ? (
                  <button
                    onClick={() => { setRawEditMode(true); if (!fullPromptDraft) setFullPromptDraft(assembledPrompt); }}
                    className="text-[11px] px-2 py-1 bg-[#1E232D] text-white rounded-md"
                    data-testid={`studio-enable-raw-${page.id}`}
                  >
                    Override raw…
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setRawEditMode(false);
                      setFullPromptDraft("");
                      if ((page.full_prompt_override || "")) updatePage({ full_prompt_override: "" });
                    }}
                    className="text-[11px] px-2 py-1 bg-[#1E232D] text-white rounded-md"
                    data-testid={`studio-reset-raw-${page.id}`}
                  >
                    Reset to assembled
                  </button>
                )}
              </div>
            </div>
            {!rawEditMode ? (
              <pre
                className="text-[11px] font-mono text-white/70 whitespace-pre-wrap bg-[#0F1115] border border-[#1E232D] rounded p-3 max-h-72 overflow-y-auto"
                data-testid={`studio-assembled-prompt-${page.id}`}
              >
{assembledPrompt || "(Assembled prompt will appear once inputs are ready.)"}
              </pre>
            ) : (
              <>
                <p className="text-[10px] text-white/40 mb-1.5">
                  When this box has any content, it is sent to the image model exactly as written — no style, no layout, no direction concat, no auto-fill. Perfect for one-off experiments.
                </p>
                <textarea
                  rows={Math.min(16, Math.max(6, (fullPromptDraft || "").split("\n").length + 1))}
                  className={inputCls + " min-h-[160px] text-[11px] font-mono leading-relaxed"}
                  value={fullPromptDraft}
                  onChange={(e) => setFullPromptDraft(e.target.value)}
                  onBlur={() => fullPromptDraft !== (page.full_prompt_override || "") && updatePage({ full_prompt_override: fullPromptDraft })}
                  placeholder="Paste the exact string you want sent to Nano Banana / GPT Image 1…"
                  data-testid={`studio-full-prompt-override-${page.id}`}
                />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ----------------------------- Cover tab ----------------------------- */
const CoverPanel = ({ story, password, onReload }) => {
  return (
    <div className="space-y-8 max-w-4xl">
      <TitleSuggestor story={story} password={password} onReload={onReload} />
      <CoverSide role="front" story={story} password={password} onReload={onReload} />
      <CoverSide role="back" story={story} password={password} onReload={onReload} />
    </div>
  );
};

const TitleSuggestor = ({ story, password, onReload }) => {
  const [loading, setLoading] = useState(false);
  const [customTitle, setCustomTitle] = useState(story.cover_title || "");
  useEffect(() => { setCustomTitle(story.cover_title || ""); }, [story.id, story.cover_title]);
  const options = story.cover_title_options || [];

  const suggest = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/studio/stories/${story.id}/suggest-titles`, {}, { ...headers(password), timeout: 60000 });
      toast.success("Titles suggested");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Title suggestion failed");
    } finally {
      setLoading(false);
    }
  };

  const persistTitle = async (t) => {
    setCustomTitle(t);
    await axios.put(`${API}/studio/stories/${story.id}`, { cover_title: t }, headers(password));
    onReload();
  };

  return (
    <SectionCard
      title="Book title"
      hint="Claude reads your captions + story summary and suggests 3 titles. Pick one, edit it freely, or write your own — this is baked into the front cover."
    >
      <div className="flex items-center gap-2">
        <input
          className={inputCls + " flex-1"}
          value={customTitle}
          onChange={(e) => setCustomTitle(e.target.value)}
          onBlur={() => customTitle !== (story.cover_title || "") && persistTitle(customTitle)}
          placeholder="Final book title…"
          data-testid="studio-cover-title-input"
        />
        <button onClick={suggest} disabled={loading} className="text-xs px-3 py-2 bg-[#1E232D] text-white rounded-md inline-flex items-center gap-1 disabled:opacity-60" data-testid="studio-suggest-titles-btn">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {options.length ? "Re-suggest" : "Suggest 3 titles"}
        </button>
      </div>
      {options.length > 0 && (
        <div className="mt-3 space-y-1.5">
          <div className="text-[11px] uppercase tracking-wider text-white/40">AI suggestions · click to use</div>
          {options.map((opt, i) => (
            <button
              key={i}
              onClick={() => persistTitle(opt)}
              className={`w-full text-left text-xs px-3 py-2 rounded border border-[#1E232D] hover:border-[#E6C982] ${customTitle === opt ? "bg-[#1E232D] text-white" : "text-white/70"}`}
              data-testid={`studio-cover-title-option-${i}`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </SectionCard>
  );
};

const CoverSide = ({ role, story, password, onReload }) => {
  const fileRef = useRef(null);
  const logoRef = useRef(null);
  const cover = story[`cover_${role}`] || {};
  const photo = (story.photos || []).find((p) => p.id === cover.photo_id);
  const selectedVersion = (cover.versions || []).find((v) => v.id === cover.selected_version_id) || (cover.versions || []).at(-1);
  const [notesDraft, setNotesDraft] = useState(story.cover_back_notes || "");
  const [extra, setExtra] = useState("");
  const [providerOverride, setProviderOverride] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [rotating, setRotating] = useState(false);
  // Cover prompt preview + raw override — mirrors PageCard.
  const [assembledPrompt, setAssembledPrompt] = useState("");
  const [assembledLoading, setAssembledLoading] = useState(false);
  const [overrideDraft, setOverrideDraft] = useState(cover.full_prompt_override || "");
  const [rawEditMode, setRawEditMode] = useState(Boolean((cover.full_prompt_override || "").trim()));
  useEffect(() => { setNotesDraft(story.cover_back_notes || ""); }, [story.id, story.cover_back_notes]);
  useEffect(() => {
    setOverrideDraft(cover.full_prompt_override || "");
    setRawEditMode(Boolean((cover.full_prompt_override || "").trim()));
  }, [story.id, role, cover.full_prompt_override]);

  const loadAssembledPrompt = async () => {
    setAssembledLoading(true);
    try {
      const r = await axios.get(
        `${API}/studio/stories/${story.id}/cover/${role}/prompt-preview`,
        headers(password)
      );
      setAssembledPrompt(r.data?.assembled_prompt || "");
    } catch {
      setAssembledPrompt("(Could not load preview)");
    } finally {
      setAssembledLoading(false);
    }
  };
  useEffect(() => {
    loadAssembledPrompt();
  }, [
    story.id, role, cover.photo_id, story.cover_title,
    story.cover_back_notes, story.custom_style_prompt,
    story.custom_layout_instructions,
    JSON.stringify(story.custom_prompts || {}),
  ]);

  const rotatePhoto = async (direction) => {
    if (!photo?.id) return;
    setRotating(true);
    try {
      await axios.post(
        `${API}/studio/stories/${story.id}/photos/${photo.id}/rotate`,
        { direction },
        { ...headers(password), timeout: 60000 }
      );
      toast.success("Rotated");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rotation failed");
    } finally {
      setRotating(false);
    }
  };

  const saveCoverOverride = async (value) => {
    try {
      await axios.patch(
        `${API}/studio/stories/${story.id}/cover/${role}`,
        { full_prompt_override: value },
        headers(password)
      );
      onReload();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const uploadPhoto = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/studio/stories/${story.id}/cover/${role}/photo`, fd, headers(password));
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const uploadLogo = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/studio/stories/${story.id}/cover/back/logo`, fd, headers(password));
      onReload();
    } catch (e) {
      toast.error("Logo upload failed");
    }
  };

  const generateCover = async () => {
    setGenerating(true);
    try {
      // Save any pending back-cover notes first.
      if (role === "back" && notesDraft !== (story.cover_back_notes || "")) {
        await axios.put(`${API}/studio/stories/${story.id}`, { cover_back_notes: notesDraft }, headers(password));
      }
      const payload = {
        role,
        ...(providerOverride ? { providers: [providerOverride] } : {}),
        ...(extra ? { extra_prompt: extra } : {}),
      };
      const r = await axios.post(`${API}/studio/stories/${story.id}/cover/illustrate`, payload, headers(password));
      const jobId = r.data?.job_id;
      if (!jobId) throw new Error("No job id returned");
      const job = await pollJob(jobId, password);
      if (job.status !== "done") throw new Error(job.error || "Cover generation failed");
      toast.success(`${role === "front" ? "Front" : "Back"} cover generated`);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Cover generation failed");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <SectionCard
      title={role === "front" ? "Front cover" : "Back cover"}
      hint={role === "front"
        ? "Upload the photo for the front cover. Claude's title (above) is baked into the illustration."
        : "Upload the back-cover photo, add your handles + website text, and optionally upload a logo. Everything except the logo is baked into the illustration; the logo is composited separately."}
    >
      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-3">
          <div className="text-[11px] uppercase tracking-wider text-white/40 mb-1">Cover photo</div>
          {photo ? (
            <img src={photo.url} alt="" className="rounded-md border border-[#1E232D] max-h-56 object-contain" />
          ) : (
            <div className="border border-dashed border-[#1E232D] rounded-md p-6 text-center text-white/40 text-xs">No photo uploaded yet.</div>
          )}
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="text-xs px-3 py-2 bg-[#1E232D] text-white rounded-md inline-flex items-center gap-1 disabled:opacity-60"
            data-testid={`studio-cover-${role}-upload-btn`}
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {photo ? "Replace photo" : "Upload photo"}
          </button>
          {photo && (
            <div className="flex items-center gap-1 pt-1">
              <span className="text-[10px] uppercase tracking-wider text-white/40 mr-1">Rotate</span>
              <button
                onClick={() => rotatePhoto("left")}
                disabled={rotating}
                className="p-1.5 bg-[#1E232D] text-white/70 hover:text-white rounded-md disabled:opacity-60"
                title="Rotate 90° left"
                data-testid={`studio-cover-${role}-rotate-left`}
              >
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => rotatePhoto("right")}
                disabled={rotating}
                className="p-1.5 bg-[#1E232D] text-white/70 hover:text-white rounded-md disabled:opacity-60"
                title="Rotate 90° right"
                data-testid={`studio-cover-${role}-rotate-right`}
              >
                <RotateCw className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => rotatePhoto("flip")}
                disabled={rotating}
                className="p-1.5 bg-[#1E232D] text-white/70 hover:text-white rounded-md disabled:opacity-60"
                title="Flip 180°"
                data-testid={`studio-cover-${role}-rotate-flip`}
              >
                <FlipVertical className="w-3.5 h-3.5" />
              </button>
              {rotating && <Loader2 className="w-3.5 h-3.5 animate-spin text-white/40 ml-1" />}
            </div>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])}
          />
          {role === "back" && (
            <div className="space-y-2 pt-2 border-t border-[#1E232D]">
              <div className="text-[11px] uppercase tracking-wider text-white/40">Back-cover text (Instagram / website / notes)</div>
              <textarea
                rows={4}
                className={inputCls + " min-h-[90px] text-xs"}
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                placeholder={"e.g., @thesnuggleprints · thesnuggleprints.com · Made with love for Bhairu, 2026"}
                data-testid="studio-cover-back-notes"
              />
              <div className="text-[11px] uppercase tracking-wider text-white/40 mt-2">Logo (optional)</div>
              {story.cover_back?.logo_url && (
                <img src={story.cover_back.logo_url} alt="logo" className="max-h-16 border border-[#1E232D] rounded" />
              )}
              <button onClick={() => logoRef.current?.click()} className="text-xs px-3 py-1.5 bg-[#1E232D] text-white rounded-md inline-flex items-center gap-1">
                <Upload className="w-3 h-3" /> {story.cover_back?.logo_url ? "Replace logo" : "Upload logo"}
              </button>
              <input ref={logoRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])} />
            </div>
          )}
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="text-[11px] uppercase tracking-wider text-white/40">Illustration</div>
            <div className="flex items-center gap-2">
              <ProviderSelect value={providerOverride} storyProviders={story.image_providers || []} onChange={setProviderOverride} testId={`studio-cover-${role}-provider`} />
              <button
                onClick={generateCover}
                disabled={generating || !photo || (role === "front" && !story.cover_title)}
                className="bg-[#E6C982] text-[#0F1115] rounded-md px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60"
                data-testid={`studio-cover-${role}-generate`}
              >
                {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                {selectedVersion ? "Regenerate" : "Generate"}
              </button>
            </div>
          </div>
          <input
            className={inputCls + " text-xs"}
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            placeholder='Optional one-off instruction: "add a warm sunset border"…'
          />
          {selectedVersion ? (
            <div className="relative group">
              <img src={selectedVersion.url} alt="" className="rounded-md border border-[#1E232D] w-full object-contain bg-black" />
              {selectedVersion.provider && (
                <div className="absolute top-2 left-2 bg-black/70 text-[10px] text-white px-2 py-0.5 rounded-full">
                  {providerLabel(selectedVersion.provider)}
                </div>
              )}
              <button
                onClick={() => downloadVersion(selectedVersion, story, role === "front" ? "cover-front" : "cover-back")}
                className="absolute top-2 right-2 bg-black/70 hover:bg-black text-white text-[10px] px-2 py-1 rounded inline-flex items-center gap-1"
              >
                <Download className="w-3 h-3" /> Download
              </button>
            </div>
          ) : (
            <div className="border border-dashed border-[#1E232D] rounded-md p-6 flex flex-col items-center gap-3 text-white/40 text-xs text-center">
              <div>{photo ? "Ready to generate, or upload an externally-created image." : "Upload the photo first."}</div>
              {photo && (
                <UploadVariantButton
                  storyId={story.id}
                  targetId={null}
                  role={role}
                  password={password}
                  onReload={onReload}
                  label="Upload variant"
                  testId={`studio-cover-${role}-upload-variant-empty`}
                />
              )}
            </div>
          )}
          {/* Version thumbnails with upload button */}
          <div className="flex items-center justify-between mb-1">
            {(cover.versions || []).length > 0 && (
              <div className="text-[10px] text-white/40">Versions ({(cover.versions || []).length})</div>
            )}
            <UploadVariantButton
              storyId={story.id}
              targetId={null}
              role={role}
              password={password}
              onReload={onReload}
              label="Upload external variant"
              testId={`studio-cover-${role}-upload-variant`}
            />
          </div>
          {(cover.versions || []).length > 1 && (
            <div className="grid grid-cols-5 gap-1.5">
              {(cover.versions || []).map((v, vi) => (
                <div key={v.id} className="relative group">
                  <button
                    onClick={() => axios.post(`${API}/studio/stories/${story.id}/cover/${role}/select-version?version_id=${v.id}`, {}, headers(password)).then(onReload)}
                    className={`aspect-square w-full rounded-sm overflow-hidden border ${cover.selected_version_id === v.id ? "border-[#E6C982]" : "border-[#1E232D]"}`}
                    title={v.edit_prompt || "original"}
                  >
                    <img src={v.url} alt="" className="w-full h-full object-cover" />
                    {cover.selected_version_id === v.id && (
                      <div className="absolute inset-0 flex items-end justify-end p-1 pointer-events-none">
                        <div className="bg-[#E6C982] rounded-full p-0.5"><Check className="w-2.5 h-2.5 text-[#0F1115]" /></div>
                      </div>
                    )}
                  </button>
                  {v.provider && (
                    <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-[9px] text-white/90 px-1 py-0.5 truncate text-center">
                      v{vi + 1} · {providerLabel(v.provider)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {/* Customer feedback */}
          {(cover.preview_feedback || "").trim() && (
            <div className="border border-[#E6C982]/30 bg-[#E6C982]/5 rounded-md p-3 mt-2">
              <div className="text-[10px] uppercase tracking-wider text-[#E6C982]/70 mb-1">Customer feedback</div>
              <p className="text-xs text-white/80 whitespace-pre-wrap">{cover.preview_feedback}</p>
            </div>
          )}
        </div>
      </div>

      {/* Final cover prompt — preview + raw override, mirrors PageCard */}
      <div className="border border-[#1E232D] rounded-md p-4 bg-[#0B0D12] mt-4">
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <div className="text-[11px] uppercase tracking-wider text-white/40">
            Final cover prompt
            {(cover.full_prompt_override || "").trim() ? (
              <span className="ml-2 text-[10px] text-[#E6C982]">Overridden · sent verbatim</span>
            ) : (
              <span className="ml-2 text-[10px] text-white/30">
                Assembled from Setup + fields above{assembledLoading ? " · refreshing…" : ""}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadAssembledPrompt}
              disabled={assembledLoading}
              className="text-[11px] px-2 py-1 bg-[#1E232D] text-white rounded-md inline-flex items-center gap-1 disabled:opacity-60"
              data-testid={`studio-cover-${role}-refresh-assembled`}
            >
              {assembledLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Refresh
            </button>
            {!rawEditMode ? (
              <button
                onClick={() => { setRawEditMode(true); if (!overrideDraft) setOverrideDraft(assembledPrompt); }}
                className="text-[11px] px-2 py-1 bg-[#1E232D] text-white rounded-md"
                data-testid={`studio-cover-${role}-enable-raw`}
              >
                Override raw…
              </button>
            ) : (
              <button
                onClick={() => {
                  setRawEditMode(false);
                  setOverrideDraft("");
                  if ((cover.full_prompt_override || "")) saveCoverOverride("");
                }}
                className="text-[11px] px-2 py-1 bg-[#1E232D] text-white rounded-md"
                data-testid={`studio-cover-${role}-reset-raw`}
              >
                Reset to assembled
              </button>
            )}
          </div>
        </div>
        {!rawEditMode ? (
          <pre
            className="text-[11px] font-mono text-white/70 whitespace-pre-wrap bg-[#0F1115] border border-[#1E232D] rounded p-3 max-h-72 overflow-y-auto"
            data-testid={`studio-cover-${role}-assembled-prompt`}
          >
{assembledPrompt || "(Assembled prompt will appear once inputs are ready.)"}
          </pre>
        ) : (
          <>
            <p className="text-[10px] text-white/40 mb-1.5">
              When this box has any content, it is sent to the image model exactly as written. Perfect for one-off cover experiments.
            </p>
            <textarea
              rows={Math.min(16, Math.max(6, (overrideDraft || "").split("\n").length + 1))}
              className={inputCls + " min-h-[160px] text-[11px] font-mono leading-relaxed"}
              value={overrideDraft}
              onChange={(e) => setOverrideDraft(e.target.value)}
              onBlur={() => overrideDraft !== (cover.full_prompt_override || "") && saveCoverOverride(overrideDraft)}
              placeholder="Paste the exact string you want sent to Nano Banana / GPT Image 1…"
              data-testid={`studio-cover-${role}-full-prompt-override`}
            />
          </>
        )}
      </div>
    </SectionCard>
  );
};

/* ----------------------------- Small helpers ----------------------------- */
const providerLabel = (id) => id === "nano_banana" ? "Nano Banana" : id === "gpt_image_1" ? "GPT Image" : (id || "");
const providerSlug = (id) => id === "nano_banana" ? "nano-banana" : id === "gpt_image_1" ? "gpt-image" : (id || "unknown");
const slugify = (s) => (s || "story").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);

const downloadVersion = async (version, story, pageLabel) => {
  // Fetch as blob so we control the filename via the anchor's `download` attr.
  try {
    const res = await fetch(version.url, { mode: "cors" });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const bookSlug = slugify(story.title || story.pet_name || "story");
    const pageSlug = typeof pageLabel === "number" ? `page-${String(pageLabel).padStart(2, "0")}` : String(pageLabel || "page");
    const modelSlug = providerSlug(version.provider);
    const filename = `${bookSlug}_${pageSlug}_${modelSlug}_${(version.id || "").slice(0, 6)}.png`;
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error("Download failed — right-click the image and save it manually.");
  }
};

const ProviderSelect = ({ value, storyProviders, onChange, testId }) => (
  <select
    value={value || "default"}
    onChange={(e) => onChange(e.target.value === "default" ? null : e.target.value)}
    className="bg-[#0F1115] border border-[#1E232D] rounded-md text-[11px] text-white px-2 py-1.5 focus:outline-none focus:border-[#E6C982]"
    title="Which model to use for this one click"
    data-testid={testId}
  >
    <option value="default">
      {storyProviders.length > 1 ? `Story default (${storyProviders.length} models)` : `Story default (${providerLabel(storyProviders[0])})`}
    </option>
    <option value="nano_banana">→ Nano Banana only</option>
    <option value="gpt_image_1">→ GPT Image only</option>
  </select>
);

const inputCls =
  "w-full bg-[#0F1115] border border-[#1E232D] rounded-md px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#E6C982]";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-wider text-white/40 mb-1.5">{label}</span>
    {children}
  </label>
);

const ChevronControl = ({ children, disabled, onClick }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`p-1.5 rounded hover:bg-[#1E232D] ${disabled ? "opacity-30 cursor-not-allowed" : ""}`}
  >
    {children}
  </button>
);

const EmptyState = ({ label, icon }) => (
  <div className="border border-dashed border-[#1E232D] rounded-xl p-12 text-center">
    <div className="text-white/30 mb-3 inline-block">{icon}</div>
    <p className="text-sm text-white/40">{label}</p>
  </div>
);

/* ------------------------ Share customer link -------------------------
 * The admin seeds a story then hands it off to the customer for photo
 * curation. Clicking the button auto-generates a share_token on the
 * story (idempotent), copies the full URL to the clipboard, and offers
 * a revoke option if a token already exists.
 */
const ShareLinkButton = ({ storyId, password, shareToken, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const shareUrl = shareToken
    ? `${window.location.origin}/curate/${storyId}/${shareToken}`
    : null;

  const generate = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/studio/stories/${storyId}/share-link`,
        {},
        headers(password)
      );
      const url = `${window.location.origin}${r.data.path}`;
      await navigator.clipboard.writeText(url);
      toast.success("Customer link copied to clipboard");
      onChanged?.();
      setOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create link");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!shareUrl) return;
    await navigator.clipboard.writeText(shareUrl);
    toast.success("Copied to clipboard");
  };

  const revoke = async () => {
    if (!confirm("Revoke this link? The customer will lose access.")) return;
    setBusy(true);
    try {
      await axios.delete(
        `${API}/studio/stories/${storyId}/share-link`,
        headers(password)
      );
      toast.success("Link revoked");
      onChanged?.();
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revoke");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={shareToken ? () => setOpen(true) : generate}
        disabled={busy}
        className="text-white/70 hover:text-[#E6C982] text-sm inline-flex items-center gap-1 border border-[#1E232D] hover:border-[#E6C982]/50 rounded-md px-3 py-1.5 transition disabled:opacity-40"
        title="Send this book to the customer for photo curation"
        data-testid="studio-copy-customer-link-btn"
      >
        {busy ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <LinkIcon className="w-3.5 h-3.5" />
        )}
        {shareToken ? "Customer link" : "Copy customer link"}
      </button>

      {open && shareUrl && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="studio-share-link-dialog">
          <div className="bg-[#0F1115] border border-[#1E232D] rounded-lg max-w-lg w-full p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-serif">Customer curation link</h3>
              <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white text-xl px-1" data-testid="studio-share-link-close">×</button>
            </div>
            <p className="text-xs text-white/50 leading-relaxed mb-3">
              Send this to the customer. They can add photos, remove any they
              don&rsquo;t like, drag to reorder, and add a caption to each one.
              Their edits show up here in the Studio in real time.
            </p>
            <div className="flex items-center gap-2 mb-4">
              <input
                readOnly
                value={shareUrl}
                onFocus={(e) => e.target.select()}
                className="flex-1 bg-[#0B0D12] border border-[#1E232D] rounded-md px-3 py-2 text-xs font-mono text-white/80 focus:outline-none focus:border-[#E6C982]/60"
                data-testid="studio-share-link-url"
              />
              <button
                onClick={copy}
                className="text-xs px-3 py-2 bg-[#E6C982] text-[#0F1115] rounded-md font-semibold hover:brightness-110"
                data-testid="studio-share-link-copy"
              >
                Copy
              </button>
            </div>
            <div className="flex items-center justify-between text-xs">
              <button
                onClick={revoke}
                disabled={busy}
                className="text-red-300/80 hover:text-red-300"
                data-testid="studio-share-link-revoke"
              >
                Revoke link
              </button>
              <a
                href={shareUrl}
                target="_blank"
                rel="noreferrer"
                className="text-white/60 hover:text-white underline"
                data-testid="studio-share-link-preview"
              >
                Preview as customer →
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};


/* ----------------------- Download book dialog ------------------------
 * Lists every image slot in the story (cover front → pages → love page
 * → empty pages → cover back) with the currently-selected version.
 * When a slot has multiple regens the admin can flip through them via a
 * dropdown before hitting Download to bundle the whole book as a ZIP. */
const DownloadBookDialog = ({ storyId, password, onClose }) => {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selections, setSelections] = useState({});
  const [downloading, setDownloading] = useState(false);
  const [fmt, setFmt] = useState("jpeg");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `${API}/studio/stories/${storyId}/download-inventory`,
          headers(password)
        );
        setItems(r.data.items || []);
        const init = {};
        for (const it of r.data.items || []) {
          const chosen = it.selected_version_id || (it.versions?.[it.versions.length - 1]?.id ?? "");
          if (chosen) init[it.slot_id] = chosen;
        }
        setSelections(init);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load download list");
      } finally {
        setLoading(false);
      }
    })();
  }, [storyId, password]);

  const totalWithImages = (items || []).filter((i) => (i.versions || []).length > 0).length;

  const download = async () => {
    setDownloading(true);
    try {
      const r = await axios.post(
        `${API}/studio/stories/${storyId}/download-book`,
        { selections, format: fmt },
        { ...headers(password), responseType: "blob", timeout: 120000 }
      );
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const cd = r.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(cd);
      const fname = match ? match[1] : `book-${storyId}.zip`;
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${fname}`);
      onClose();
    } catch (e) {
      let detail = "Download failed";
      if (e?.response?.data) {
        try {
          const text = await e.response.data.text();
          detail = JSON.parse(text)?.detail || detail;
        } catch { /* ignore parse */ }
      }
      toast.error(detail);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="studio-download-dialog">
      <div className="bg-[#0F1115] border border-[#1E232D] rounded-lg max-w-2xl w-full max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#1E232D]">
          <div>
            <h3 className="text-lg font-serif">Download book as ZIP</h3>
            <p className="text-xs text-white/50 mt-0.5">
              {loading ? "Loading images…" :
                `${totalWithImages} image${totalWithImages === 1 ? "" : "s"} ready.` +
                " Pick which version of each slot to include."}
            </p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white text-xl px-2" data-testid="studio-download-close">×</button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-white/40" />
            </div>
          ) : (items || []).length === 0 ? (
            <p className="text-sm text-white/40 text-center py-8">
              No slots found. Add some pages first.
            </p>
          ) : (
            items.map((it) => {
              const chosenId = selections[it.slot_id];
              const chosen = (it.versions || []).find((v) => v.id === chosenId);
              const hasImages = (it.versions || []).length > 0;
              return (
                <div
                  key={it.slot_id}
                  className={`flex items-center gap-3 border rounded-lg p-2.5 ${
                    hasImages ? "border-[#1E232D]" : "border-[#1E232D]/40 opacity-50"
                  }`}
                  data-testid={`studio-download-slot-${it.slot_id}`}
                >
                  <div className="w-14 h-14 shrink-0 bg-[#1A1F28] rounded overflow-hidden flex items-center justify-center">
                    {chosen?.url ? (
                      <img src={chosen.url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon className="w-5 h-5 text-white/30" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{it.label}</div>
                    <div className="text-[11px] text-white/40 mt-0.5">
                      {hasImages ? `${it.versions.length} version${it.versions.length === 1 ? "" : "s"}` : "no images yet"}
                    </div>
                  </div>
                  {hasImages && (
                    <select
                      value={chosenId || ""}
                      onChange={(e) => setSelections((prev) => ({ ...prev, [it.slot_id]: e.target.value }))}
                      className="text-xs bg-[#0B0D12] border border-[#1E232D] rounded px-2 py-1.5 text-white/80 focus:outline-none focus:border-[#E6C982]/60 max-w-[180px]"
                      data-testid={`studio-download-select-${it.slot_id}`}
                    >
                      {it.versions.map((v, vidx) => (
                        <option key={v.id} value={v.id}>
                          v{vidx + 1}
                          {v.id === it.selected_version_id ? " · picked" : ""}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="border-t border-[#1E232D] px-5 py-3 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-wider text-white/40">Format:</span>
            {[{ v: "jpeg", l: "JPEG" }, { v: "png", l: "PNG" }].map((o) => (
              <label key={o.v} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded border cursor-pointer text-xs transition ${fmt === o.v ? "border-[#E6C982] text-[#E6C982]" : "border-[#1E232D] text-white/50 hover:border-white/20"}`}>
                <input type="radio" name="book_fmt" value={o.v} checked={fmt === o.v} onChange={() => setFmt(o.v)} className="accent-[#E6C982]" />
                {o.l}
              </label>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="text-sm px-4 py-2 border border-[#1E232D] rounded-md text-white/70 hover:text-white hover:border-white/30"
              data-testid="studio-download-cancel"
            >
              Cancel
            </button>
            <button
              onClick={download}
              disabled={loading || downloading || totalWithImages === 0}
              className="text-sm px-5 py-2 bg-[#E6C982] text-[#0F1115] rounded-md font-semibold disabled:opacity-40 inline-flex items-center gap-2"
              data-testid="studio-download-go"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {downloading ? "Zipping…" : `Download (${totalWithImages})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

